import {
	Stack,
	Button,
	Inline,
	Divider,
	Grid,
	Col,
	Heading,
	Text,
	Box,
	Alert,
	Spinner,
	Icons,
	Badge,
	AsyncSelect,
	Select,
	Table,
	Tabs,
	TabsList,
	TabContent,
	Component,
	Ping,
	Dropdown,
	joinAttributes,
	Tooltip,
	Time,
} from "@sembark-travel/ui-base"
import { useCallback, useEffect, useRef, useState } from "react"
import { Optional } from "utility-types"
import * as Validator from "yup"
import {
	TNewIntegrationSchema,
	TWhatsAppAutomationTemplate,
	TWAIntegratedApp,
	TWAEditorBootstrapResponse,
	TWAEditorTemplateResponse,
	TWATemplate,
	TWhatsAppIntegration,
	TWhatsappBusinessAccount,
	TWhatsappBusinessAccountPhoneNumber,
	TWAWebhookHealth,
	TWAWebhookHealthAccount,
	TWAWebhookHealthCheck,
	TWAWebhookHealthStatus,
	useFetchWATemplatePreview,
	waTemplateSourceMismatch,
} from "./store"
import {
	useFetchWAIntegratedApp,
	locationQueryToTemplateFilters,
	templateFiltersToLocationQuery,
	templateFiltersToRequestParams,
	flowFiltersToLocationQuery,
	flowFiltersToRequestParams,
	locationQueryToFlowFilters,
	type IFlowFilters,
	type IFlowFiltersInLocationQuery,
	type ITemplateFilters,
	type ITemplateFiltersInLocationQuery,
	type TWAFlowRecord,
	type TWAFlowSubmittedField,
	type TWAFlowPreset,
	WA_FLOW_CATEGORIES,
} from "./store"
import {
	Form,
	withServerErrors,
	validateFormValues,
	TextInputField,
	SubmissionError,
	TextAreaInputField,
	CheckboxInputField,
	SelectField,
	SelectInputField,
	GetFieldValue,
	useForm,
	isTruthy,
} from "@sembark-travel/ui-form"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { trackWhatsAppMessageSent } from "@sembark-travel/tracking"
import { PlatformIcon } from "./PlatformIcon"
import { IListResponse, useXHR } from "@sembark-travel/xhr"
import { utcTimestampToLocalTimeString } from "@sembark-travel/datetime-utils"
import useSWR from "swr"
import { useOutletContext } from "react-router"
import {
	ButtonLink,
	NavLink,
	useLocationQuery,
} from "@sembark-travel/ui-router"
import { generatePath, Link } from "../router-utils"
import {
	ListView,
	Search,
	type TSearchParams,
	useSearch,
} from "@sembark-travel/ui-list"
import { JSONPreview, JSONPreviewInDialog } from "./JsonPreview"
import {
	IWhatsAppTemplateEditorValue,
	TWhatsAppTemplateSampleMedia,
	TWhatsAppMessage,
	WhatsAppTemplateInputField,
} from "../WhatsAppMessages"
import { SelectTeams } from "../Teams"

type TNewWhatsAppCloudApiIntegrationSchema = Pick<
	TNewIntegrationSchema,
	"name" | "description" | "platform" | "in_test_mode"
> & {
	/** The number test-mode messages go to. Required when starting in test mode. */
	whatsapp?: { test_recipient?: string }
}

const newWhatsAppCloudApiIntegrationSchemaValidator = Validator.object().shape({
	name: Validator.string()
		.required("Please give a name to your integration")
		.max(50, "Please use 50 or fewer characters for the name"),
	description: Validator.string()
		.required("Please provide a description for this integration")
		.max(500, "Please use 500 or fewer characters for the description"),
	whatsapp: Validator.object().when("in_test_mode", {
		is: (in_test_mode: boolean) => Boolean(in_test_mode),
		then: (schema) =>
			schema.shape({
				test_recipient: Validator.string()
					.required("Please enter the number to deliver test messages to")
					.matches(
						/^\d{8,15}$/,
						"Digits only, with the country code and no plus sign, e.g. 919876543210"
					),
			}),
	}),
})

export function NewWhatsAppCloudApiIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewWhatsAppCloudApiIntegrationSchema>
	onSubmit: (data: TNewWhatsAppCloudApiIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewWhatsAppCloudApiIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			platform: "whatsapp_cloud_api" as const,
			in_test_mode: false,
			whatsapp: { test_recipient: "" },
		}))

	return (
		<Form<TNewWhatsAppCloudApiIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(
				newWhatsAppCloudApiIntegrationSchemaValidator
			)}
			onSubmit={withServerErrors(
				async (values: TNewWhatsAppCloudApiIntegrationSchema) => {
					const inTestMode = Boolean(values.in_test_mode)
					await onSubmit({
						...values,
						in_test_mode: inTestMode ? 1 : 0,
						whatsapp: inTestMode
							? {
									test_recipient: values.whatsapp?.test_recipient?.trim(),
								}
							: undefined,
					})
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4} position="relative">
								<Stack gap="2" position="sticky" top="0">
									<Inline gap="4">
										<Box>
											<PlatformIcon platform="whatsapp_cloud_api" size="40" />
										</Box>
										<Heading>WhatsApp Cloud API Integration</Heading>
									</Inline>
									<Text>
										Connect your WhatsApp Business Account to send and receive
										messages. You'll complete the connection setup after
										creating this integration.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<Stack gap="4">
										<TextInputField
											name="name"
											label="Integration Name"
											placeholder="e.g., Customer Support WhatsApp"
											required
										/>
										<TextAreaInputField
											name="description"
											label="Description"
											placeholder="Describe the purpose of this integration"
											required
											rows={3}
										/>
										<CheckboxInputField
											name="in_test_mode"
											label="Start in test mode"
											help="Every message goes to a test number on your team, never to a customer. You can turn this off any time."
										/>
										<GetFieldValue<boolean> name="in_test_mode">
											{({ value: inTestMode }) =>
												inTestMode ? (
													<TextInputField
														name="whatsapp.test_recipient"
														label="Test Number"
														secondaryLabel="with country code, without +/-"
														placeholder="919876543210"
														inputMode="numeric"
														maxWidth="xs"
														required
														help={
															<Text maxWidth="lg">
																Templates reach it any time; free-form replies
																and attachments only within 24 hours of it
																messaging your business number, as WhatsApp
																allows.
															</Text>
														}
													/>
												) : null
											}
										</GetFieldValue>
									</Stack>
								</Stack>
							</Col>
						</Grid>
						<Divider />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Creating Integration..." : "Create Integration"}
							</Button>
							<Button
								type="button"
								disabled={submitting}
								onClick={onCancel}
								level="tertiary"
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

type TNewWhatsAppSimulatorIntegrationSchema = Pick<
	TNewIntegrationSchema,
	"name" | "description" | "platform"
>

const newWhatsAppSimulatorIntegrationSchemaValidator = Validator.object().shape(
	{
		name: Validator.string()
			.required("Please give a name to your integration")
			.max(50, "Please use 50 or fewer characters for the name"),
		description: Validator.string()
			.required("Please provide a description for this integration")
			.max(500, "Please use 500 or fewer characters for the description"),
	}
)

export function NewWhatsAppSimulatorIntegrationForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
}: {
	initialValues?: Optional<TNewWhatsAppSimulatorIntegrationSchema>
	onSubmit: (data: TNewWhatsAppSimulatorIntegrationSchema) => Promise<unknown>
	onCancel: () => void
}) {
	const [newIntegrationInitialValues] =
		useState<TNewWhatsAppSimulatorIntegrationSchema>(() => ({
			name: propInitialValues?.name || "",
			description: propInitialValues?.description || "",
			platform: "whatsapp_simulator" as const,
		}))

	return (
		<Form<TNewWhatsAppCloudApiIntegrationSchema>
			initialValues={newIntegrationInitialValues}
			validate={validateFormValues(
				newWhatsAppSimulatorIntegrationSchemaValidator
			)}
			onSubmit={withServerErrors(
				async (values: TNewWhatsAppCloudApiIntegrationSchema) => {
					await onSubmit({
						...values,
					})
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Grid gap="6">
							<Col sm={12} md={4} position="relative">
								<Stack gap="2" position="sticky" top="0">
									<Inline gap="4">
										<Box>
											<PlatformIcon platform="whatsapp_simulator" size="40" />
										</Box>
										<Heading>WhatsApp API Simulator Integration</Heading>
									</Inline>
									<Text>
										Connect your WhatsApp Business Account to send and receive
										messages. You'll complete the connection setup after
										creating this integration.
									</Text>
								</Stack>
							</Col>
							<Col>
								<Stack gap="4">
									<Stack gap="4">
										<TextInputField
											name="name"
											label="Integration Name"
											placeholder="e.g., Customer Support WhatsApp"
											required
										/>
										<TextAreaInputField
											name="description"
											label="Description"
											placeholder="Describe the purpose of this integration"
											required
											rows={3}
										/>
									</Stack>
								</Stack>
							</Col>
						</Grid>
						<Divider />
						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Creating Integration..." : "Create Integration"}
							</Button>
							<Button
								type="button"
								disabled={submitting}
								onClick={onCancel}
								level="tertiary"
							>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}

export function WhatsAppCloudApiIntegratedApp({
	integration,
	children,
}: {
	integration: TWhatsAppIntegration
	children?: (props: { integration: TWhatsAppIntegration }) => React.ReactNode
}) {
	const { integrated_app_id: integratedAppId } = integration
	const {
		data,
		error,
		mutate: refresh,
	} = useFetchWAIntegratedApp(integratedAppId)

	if (!data) {
		if (error) {
			return (
				<Alert status="error">
					{error?.message || "Something went wrong. Please try after sometime."}
				</Alert>
			)
		}
		return <Spinner alignCenter padding="4" />
	}

	const {
		data: integratedApp,
		meta: {
			embedded_signup_url,
			legacy_auth_url,
			legacy_reauth_url,
			exception,
		},
	} = data

	// A first connection goes through Embedded Signup once the server has a
	// configuration for it. Until then the login redirect still works.
	const auth_url = embedded_signup_url ?? legacy_auth_url

	// The login redirect stays reachable beside it. Meta will not let the
	// business that owns the app go through Embedded Signup, so Sembark's
	// own number needs this path.
	const legacy_auth_url_alt = embedded_signup_url ? legacy_auth_url : null

	// A reconnect uses the flow the integration was connected with — its
	// token, its business scope — and offers the other one as an alternative.
	const embeddedSignupReauth: TReauthAction | null = embedded_signup_url
		? {
				url: embedded_signup_url,
				label: "Reconnect with Embedded Signup",
				title: "Meta's onboarding dialog. Use this for a customer's business.",
			}
		: null
	const facebookLoginReauth: TReauthAction = {
		url: legacy_reauth_url,
		label: "Reconnect with Facebook Login",
		title:
			"Use this for the business that owns the Meta app — Meta does not allow it through Embedded Signup.",
	}
	const [reauth, alternateReauth]: [TReauthAction, TReauthAction | null] =
		integratedApp.connection_method === "facebook_login" ||
		!embeddedSignupReauth
			? [facebookLoginReauth, embeddedSignupReauth]
			: [embeddedSignupReauth, facebookLoginReauth]

	const isConnected = Boolean(
		integratedApp.wa_business_account && integratedApp.wa_phone_number
	)

	return (
		<Stack gap="4">
			<WhatsAppIntegratedAppOverview
				name={integration.name}
				description={integration.description}
				integratedApp={integratedApp}
				authUrl={auth_url}
				reauthUrl={reauth.url}
				legacyAuthUrl={legacy_auth_url_alt}
				alternateReauth={alternateReauth}
				exception={exception}
				refresh={refresh}
			/>

			{isConnected ? (
				<Box borderWidth="1" rounded="md">
					<Tabs layout="row">
						<TabsList roundedLeft="md">
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/templates",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Templates
							</NavLink>
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/flows",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Flows
							</NavLink>
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/automations",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Automations
							</NavLink>
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/quick-sends",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Quick Sends
							</NavLink>
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/accounts",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Accounts
							</NavLink>
							<NavLink
								to={generatePath(
									"/integrations/:integrationId/whatsapp/messages",
									{
										integrationId: integration.uid,
									}
								)}
							>
								Messages
							</NavLink>
						</TabsList>
						<TabContent>
							<Box>
								{children?.({
									integration,
								})}
							</Box>
						</TabContent>
					</Tabs>
				</Box>
			) : null}
		</Stack>
	)
}

/**
 * One way of re-authorising — the flow, where it lives, and how to describe
 * it when it is offered as the alternative rather than the default.
 */
type TReauthAction = {
	url: string
	label: string
	title: string
}

export function WhatsAppIntegratedAppOverview({
	integratedApp,
	authUrl,
	reauthUrl,
	legacyAuthUrl,
	alternateReauth,
	exception,
	name,
	description,
	refresh,
}: {
	integratedApp: TWAIntegratedApp
	authUrl: string
	/** The reconnect that matches how the integration was connected. */
	reauthUrl: string
	/**
	 * The Facebook Login redirect, offered beside Embedded Signup for a first
	 * connection. Null when Embedded Signup is not configured, since `authUrl`
	 * is then already it.
	 */
	legacyAuthUrl?: string | null
	/** The other reconnect flow, when there is one to offer. */
	alternateReauth?: TReauthAction | null
	exception?: Record<string, unknown>
	name: string
	description?: string
	refresh: () => void
}) {
	return (
		<Box borderWidth="1" rounded="md" padding="4">
			<Stack gap="4">
				<WhatsAppConnectionStatus
					integratedApp={integratedApp}
					authUrl={authUrl}
					exception={exception}
					reauthUrl={reauthUrl}
					legacyAuthUrl={legacyAuthUrl}
					alternateReauth={alternateReauth}
					name={name}
					description={description}
					refresh={refresh}
				/>
				<Divider marginY="0" />
				<WhatsAppDefaultTeamSetting
					integratedApp={integratedApp}
					onUpdated={refresh}
				/>
				{integratedApp.provider === "whatsapp_cloud" ? (
					<>
						<Divider marginY="0" />
						{integratedApp.integration?.in_test_mode_at ? (
							<Alert status="warning" title="Test mode">
								<Stack gap="2">
									{integratedApp.test_recipient ? (
										<Text>
											Every message from this integration is delivered to{" "}
											<Text as="span" fontWeight="semibold">
												+{integratedApp.test_recipient}
											</Text>{" "}
											instead of the customer.
										</Text>
									) : (
										<Text>
											No test number is set yet, so every send is refused. Set
											one, or turn test mode off.
										</Text>
									)}
									<WhatsAppTestModeSetting
										integratedApp={integratedApp}
										onUpdated={refresh}
									>
										{({ toggle, editNumber }) => (
											<Inline gap="4">
												{integratedApp.test_recipient ? (
													<>
														<Button
															onClick={toggle}
															status="warning"
															level="primary"
														>
															Disable Test Mode
														</Button>
														<Button onClick={editNumber} level="secondary">
															Change Number
														</Button>
													</>
												) : (
													<Button onClick={editNumber} level="primary">
														Set Test Number
													</Button>
												)}
											</Inline>
										)}
									</WhatsAppTestModeSetting>
								</Stack>
							</Alert>
						) : null}
					</>
				) : null}
			</Stack>
		</Box>
	)
}

/**
 * While on, every outbound message of this integration — trip page, thread
 * replies, template test sends, automations — is delivered to the test
 * number instead of the customer. The record still sits on the customer's
 * thread, badged with where it went.
 */
function WhatsAppTestModeSetting({
	integratedApp,
	onUpdated,
	children,
}: {
	integratedApp: TWAIntegratedApp
	onUpdated: () => void
	/**
	 * `toggle` opens the dialog that flips test mode: the number form when
	 * turning on (or when on without a number), the confirmation when turning
	 * off. `editNumber` opens the number form while test mode stays on.
	 */
	children: (props: {
		toggle: () => void
		editNumber: () => void
	}) => React.ReactNode
}) {
	const xhr = useXHR()
	const inTestMode = Boolean(integratedApp.integration?.in_test_mode_at)
	const testRecipient = integratedApp.test_recipient || ""
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [isEditingNumber, setIsEditingNumber] = useState(false)

	const update = async (enabled: boolean, test_recipient?: string) => {
		await xhr.patch(`/integrations/whatsapp/${integratedApp.id}/test-mode`, {
			enabled: isTruthy(enabled) ? 1 : 0,
			test_recipient,
		})
		showSnackbar(
			!enabled
				? "Test mode is off. Messages go to customers."
				: inTestMode
					? `Test number updated. Messages go to +${test_recipient}.`
					: `Test mode is on. Messages go to +${test_recipient}.`
		)
		onUpdated()
	}

	// Turning off is the only path that needs no number, and it needs a
	// confirmation instead. Everything else — turning on, setting a missing
	// number, changing it — is the number form.
	const showNumberForm = !inTestMode || !testRecipient || isEditingNumber
	const close = () => {
		closeDialog()
		setIsEditingNumber(false)
	}

	return (
		<>
			{children({
				toggle: () => {
					setIsEditingNumber(false)
					openDialog()
				},
				editNumber: () => {
					setIsEditingNumber(true)
					openDialog()
				},
			})}
			<Dialog
				open={Boolean(isOpen && !showNumberForm)}
				title="Disable Test Mode"
				onClose={close}
				sm
			>
				<Form<unknown>
					onSubmit={withServerErrors(async () => {
						await update(false)
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Stack gap="4">
									<Text>
										Messages from this integration currently go to{" "}
										<Text as="span" fontWeight="semibold">
											+{testRecipient}
										</Text>
										. Once test mode is off:
									</Text>
									<Stack
										as="ul"
										gap="1"
										paddingLeft="6"
										listStyleType="decimal"
									>
										<Text as="li">
											Customers start receiving messages — from the trip page,
											conversation replies, and every active automation.
										</Text>
										<Text as="li">
											Automations keep running as they are; check their
											conditions and templates before turning this off.
										</Text>
										<Text as="li">
											Messages already delivered to the test number stay marked
											as test messages in their conversations.
										</Text>
										<Text as="li">
											The test number is remembered, so turning test mode on
											again needs no re-entry.
										</Text>
									</Stack>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button
											type="submit"
											status="warning"
											disabled={submitting}
										>
											{submitting ? "Disabling..." : "Disable Test Mode"}
										</Button>
										<Button disabled={submitting} onClick={close}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
			<Dialog
				open={Boolean(isOpen && showNumberForm)}
				title={
					!inTestMode
						? "Enable Test Mode"
						: testRecipient
							? "Change Test Number"
							: "Set Test Number"
				}
				onClose={close}
				sm
			>
				<Form<{ test_recipient: string }>
					initialValues={{ test_recipient: testRecipient }}
					validate={validateFormValues(
						Validator.object().shape({
							test_recipient: Validator.string()
								.required("Please enter the number to deliver test messages to")
								.matches(
									/^\d{8,15}$/,
									"Digits only, with the country code and no plus sign, e.g. 919876543210"
								),
						})
					)}
					onSubmit={withServerErrors(async (values) => {
						await update(true, values.test_recipient.trim())
						close()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting }) => (
						<form onSubmit={handleSubmit} noValidate>
							<Dialog.Body>
								<Stack gap="4">
									{inTestMode && !testRecipient ? (
										<Alert status="warning">
											Test mode is on without a number, so every send is refused
											until one is set.
										</Alert>
									) : null}
									<TextInputField
										name="test_recipient"
										label="Test Number"
										secondaryLabel="with country code, without +/-"
										placeholder="919999999999"
										inputMode="numeric"
										required
										help="A phone on your team that you can check."
									/>
									<Stack gap="2">
										<Text fontWeight="semibold">While test mode is on</Text>
										<Stack as="ul" gap="1" paddingLeft="6" listStyleType="disc">
											<Text as="li">
												Every message from this integration — trip page,
												conversation replies, template test sends, and
												automations — is delivered to this number instead of the
												customer. Customers receive nothing.
											</Text>
											<Text as="li">
												Each message is still recorded on the customer's
												conversation, marked as sent to the test number, so
												history reads as it will in live mode.
											</Text>
											<Text as="li" fontWeight="semibold">
												Delivery is real, through WhatsApp, so the usual
												conversation charges apply.
											</Text>
											<Text as="li">
												Templates reach the test number any time. Free-form
												replies and attachments only go through within 24 hours
												of that phone messaging your business number, as
												WhatsApp requires.
											</Text>
											<Text as="li">
												Replies from the test phone show up as its own
												conversation, not the customer's.
											</Text>
											<Text as="li">
												Templates, sync, and the health check are unaffected.
											</Text>
										</Stack>
									</Stack>
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Stack gap="4">
									<SubmissionError />
									<Inline gap="4">
										<Button type="submit" disabled={submitting}>
											{submitting
												? "Saving..."
												: !inTestMode
													? "Enable Test Mode"
													: testRecipient
														? "Update Number"
														: "Set Number"}
										</Button>
										<Button disabled={submitting} onClick={close}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

/**
 * Picks the team whose active members are seeded as subscribers of every new
 * inbound conversation on this integration. Unset → new conversations fall
 * back to the tenant's sales heads.
 */
function WhatsAppDefaultTeamSetting({
	integratedApp,
	onUpdated,
}: {
	integratedApp: TWAIntegratedApp
	onUpdated: () => void
}) {
	const xhr = useXHR()
	const [isEditing, setIsEditing] = useState(!integratedApp.default_team)
	const save = async (teamId: number | null) => {
		await xhr.patch(`/integrations/whatsapp/${integratedApp.id}/default-team`, {
			default_team: teamId,
		})
		showSnackbar(teamId ? "Default team updated." : "Default team removed.")
		onUpdated()
		setIsEditing(false)
	}

	return (
		<Inline gap={{ xs: "2", lg: "4" }} flexWrap="wrap">
			<Text fontWeight="semibold">Default team for new conversations:</Text>
			{!isEditing ? (
				<Inline gap="4">
					{integratedApp.default_team ? (
						<Text color="accent">
							<Link
								to="/teams/:teamId"
								params={{ teamId: String(integratedApp.default_team.id) }}
							>
								{integratedApp.default_team.name}
							</Link>
						</Text>
					) : (
						<Inline gap="1">
							<Text as="span" fontWeight="semibold">
								Not Set.
							</Text>
							<Text color="muted">
								All new notifications will go to sales heads and admins.
							</Text>
						</Inline>
					)}
					<Button
						level="tertiary"
						size="sm"
						inline
						onClick={() => setIsEditing(true)}
					>
						<Icons.Pencil />
					</Button>
				</Inline>
			) : (
				<>
					<Form
						initialValues={{ default_team: integratedApp.default_team }}
						onSubmit={withServerErrors(async (values) => {
							await save(values.default_team?.id || null)
						})}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form onSubmit={handleSubmit} noValidate>
								<Stack gap="2">
									<SelectField
										size="sm"
										name="default_team"
										select={SelectTeams}
										disabled={submitting}
										help={
											<Text maxWidth="lg">
												New WhatsApp conversations on this integration notify
												this team's members. If unset or the team has no active
												members, they go to all sales heads.
											</Text>
										}
									/>
									<Inline gap="2">
										<Button type="submit" size="sm">
											{submitting ? "Updating..." : "Update"}
										</Button>
										<Button size="sm" onClick={() => setIsEditing(false)}>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</>
			)}
		</Inline>
	)
}

/**
 * Re-runs discovery against Meta with the stored token — business accounts,
 * numbers, webhook subscriptions — without sending the operator through a
 * dialog. Picks up a number added after connecting and clears a stale
 * setup failure. Reconnect is for a lost grant; this is for everything else.
 */
function SyncWithMetaMenuItem({
	integratedAppId,
	onQueued,
}: {
	integratedAppId: number
	onQueued: () => void
}) {
	const xhr = useXHR()
	const [isSyncing, setIsSyncing] = useState(false)

	return (
		<Dropdown.MenuItem
			onClick={async (e: React.MouseEvent) => {
				e.preventDefault()
				if (isSyncing) return
				setIsSyncing(true)
				try {
					await xhr.post(`/integrations/whatsapp/${integratedAppId}/sync`)
					showSnackbar("Syncing with Meta. This takes a few seconds.")
					onQueued()
				} catch (error) {
					showSnackbar((error as Error)?.message || "Could not start the sync.")
				} finally {
					setIsSyncing(false)
				}
			}}
		>
			<Icons.Refresh /> {isSyncing ? "Syncing…" : "Sync with Meta"}
		</Dropdown.MenuItem>
	)
}

function WhatsAppConnectionStatus({
	integratedApp,
	authUrl,
	reauthUrl,
	legacyAuthUrl,
	alternateReauth,
	exception,
	name,
	refresh,
}: Pick<
	React.ComponentProps<typeof WhatsAppIntegratedAppOverview>,
	| "integratedApp"
	| "authUrl"
	| "reauthUrl"
	| "legacyAuthUrl"
	| "alternateReauth"
	| "exception"
	| "name"
	| "description"
	| "refresh"
>) {
	const isConnected = Boolean(
		integratedApp.wa_business_account && integratedApp.wa_phone_number
	)

	const connectionInProcess =
		integratedApp.user_access_token_issued_at && !isConnected

	useSWR(connectionInProcess ? "refreshing" : null, () => refresh(), {
		refreshInterval: 2000,
	})

	return (
		<Stack gap="4">
			<Inline alignItems="start" justifyContent="between" gap="4">
				<Inline gap="4" alignItems="start" flex="1" minWidth="0">
					<Box flexShrink="0">
						<PlatformIcon platform="whatsapp_cloud_api" size="48" />
					</Box>
					<Stack gap="2" flex="1" minWidth="0">
						<Heading fontSize="lg">{name}</Heading>
						{isConnected ? (
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Badge success>
									<Icons.OkCircle /> Connected
								</Badge>
								{integratedApp.connection_method ? (
									<Badge
										title={
											integratedApp.connection_method === "embedded_signup"
												? "Connected through Meta's Embedded Signup dialog. The token does not expire."
												: "Connected through Facebook Login. The token is refreshed on a schedule."
										}
									>
										{integratedApp.connection_method === "embedded_signup"
											? "Embedded Signup"
											: "Facebook Login"}
									</Badge>
								) : null}
								{integratedApp.user_access_token_issued_at ? (
									<Text fontSize="sm" color="muted">
										on{" "}
										<Time
											timestamp={integratedApp.user_access_token_issued_at}
										/>
									</Text>
								) : null}
							</Inline>
						) : connectionInProcess ? (
							<Text color="success">
								<Badge>Connecting...</Badge> Please wait for a few seconds.
							</Text>
						) : (
							<Stack gap="2">
								<Box>
									<Text fontSize="sm" color="muted">
										Connect your WhatsApp Business Account to start sending
										automated messages.
									</Text>
								</Box>
								{authUrl ? (
									<Inline gap="3" alignItems="center" flexWrap="wrap">
										<Button
											as="a"
											href={authUrl}
											target="_blank"
											rel="noreferrer noopener"
											level="primary"
										>
											Connect WhatsApp Business
										</Button>
										{legacyAuthUrl ? (
											<Text
												as="a"
												href={legacyAuthUrl}
												target="_blank"
												rel="noreferrer noopener"
												fontSize="sm"
												color="muted"
												title="Use this for the business that owns the Meta app — Meta does not allow it through Embedded Signup."
											>
												Connect with Facebook Login instead
											</Text>
										) : null}
									</Inline>
								) : null}
							</Stack>
						)}
						{integratedApp.wa_business_accounts?.length ? (
							<Inline gap="1" flexWrap="wrap">
								{integratedApp.wa_business_accounts.map((waba) => (
									<Badge
										key={waba.id}
										success={waba.account_review_status === "APPROVED"}
										warning={waba.account_review_status === "RESTRICTED"}
										danger={waba.account_review_status === "REJECTED"}
									>
										<Text
											as="span"
											title={`Business: ${waba.fb_business_account?.name} • BID: ${waba.fb_business_account?.fb_business_account_id} • WABA ID: ${waba.wa_business_account_id}`}
										>
											{waba.name}
										</Text>{" "}
										(
										{joinAttributes(
											...waba.wa_phone_numbers.map(
												(ph: TWhatsappBusinessAccountPhoneNumber) => (
													<Text
														as="span"
														title={`Ph. ID: ${ph.wa_phone_number_id} (Status: ${ph.status}, QUALITY: ${ph.quality_rating})${
															ph.two_step_pin
																? ` • 2-step PIN: ${ph.two_step_pin}`
																: ""
														}${
															ph.is_on_biz_app
																? " • Also used in the WhatsApp Business app"
																: ""
														}`}
														key={ph.id}
													>
														<Box
															as="a"
															href={`https://business.facebook.com/latest/whatsapp_manager/phone_numbers?business_id=${waba.fb_business_account?.fb_business_account_id}&asset_id=${waba.wa_business_account_id}`}
															target="_blank"
															rel="noopener noreferrer"
															color={
																ph.status !== "CONNECTED" ? "danger" : undefined
															}
														>
															{ph.display_phone_number}
														</Box>
														{ph.is_on_biz_app ? " · Business app" : null}
													</Text>
												)
											)
										)}
										)
									</Badge>
								))}
							</Inline>
						) : null}
					</Stack>
				</Inline>

				{integratedApp.user_access_token_issued_at &&
				integratedApp.provider !== "whatsapp_simulator" ? (
					<Box flexShrink="0">
						<Dropdown alignRight>
							<Dropdown.ToggleButton level="tertiary">
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								<li>
									<a href={reauthUrl} target="_blank" rel="noreferrer noopener">
										<Icons.SwitchHorizontal /> Reconnect WhatsApp Account
									</a>
								</li>
								<SyncWithMetaMenuItem
									integratedAppId={integratedApp.id}
									onQueued={refresh}
								/>
								<WhatsAppConnectionHealth integratedAppId={integratedApp.id}>
									{({ openDialog }) => (
										<Dropdown.MenuItem
											onClick={(e) => {
												e.preventDefault()
												openDialog()
											}}
										>
											<Icons.ShieldCheck /> Check Connection Health
										</Dropdown.MenuItem>
									)}
								</WhatsAppConnectionHealth>
								<Dropdown.MenuItemDivider></Dropdown.MenuItemDivider>

								<WhatsAppTestModeSetting
									integratedApp={integratedApp}
									onUpdated={refresh}
								>
									{({ toggle }) => (
										<Dropdown.MenuItem
											onClick={(e) => {
												e.preventDefault()
												toggle()
											}}
										>
											<Icons.TestTubeDiagonal />{" "}
											{integratedApp.integration?.in_test_mode_at
												? "Disable"
												: "Enable"}{" "}
											Test Mode
										</Dropdown.MenuItem>
									)}
								</WhatsAppTestModeSetting>
								{alternateReauth ? (
									<>
										<Dropdown.MenuItemDivider></Dropdown.MenuItemDivider>
										<li>
											<a
												href={alternateReauth.url}
												target="_blank"
												rel="noreferrer noopener"
												title={alternateReauth.title}
											>
												<Icons.SwitchHorizontal /> {alternateReauth.label}
											</a>
										</li>
									</>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					</Box>
				) : null}
			</Inline>

			{exception ? (
				<Alert status="error">
					<Stack gap="4">
						<Stack gap="1">
							<Heading fontSize="md" color="danger">
								<Icons.Attention size="6" /> Connection Failure{" "}
								<Ping color="danger" />
							</Heading>
							{typeof exception.message === "string" ? (
								<Text color="danger">{exception.message}</Text>
							) : null}
							<Text color="muted">
								Something went wrong when connecting with your WhatsApp account.
								Please try to reconnect with your account to fix any issues.
							</Text>
						</Stack>
						<Inline gap="4">
							<Button
								as="a"
								href={reauthUrl}
								target="_blank"
								rel="noreferrer noopener"
								level="primary"
							>
								<Icons.SwitchHorizontal /> Reconnect WhatsApp Account
							</Button>
							{alternateReauth ? (
								<Button
									as="a"
									href={alternateReauth.url}
									target="_blank"
									rel="noreferrer noopener"
									level="secondary"
									title={alternateReauth.title}
								>
									<Icons.SwitchHorizontal /> {alternateReauth.label}
								</Button>
							) : null}
						</Inline>
						<JSONPreview
							data={exception}
							bgColor="danger"
							color="danger"
							borderColor="danger"
						/>
					</Stack>
				</Alert>
			) : null}
		</Stack>
	)
}

/**
 * Reports whether Meta's webhooks can actually reach this integration. Every
 * check is listed, healthy ones included, so an operator can tell "all good"
 * apart from "we never looked".
 *
 * The endpoint reads live from Meta and is throttled, so it is only fetched
 * once the dialog is open, and re-checking is an explicit action.
 */
export function WhatsAppConnectionHealth({
	integratedAppId,
	children,
}: {
	integratedAppId: number
	children: (props: { openDialog: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const {
		data,
		error,
		isValidating,
		mutate: recheck,
	} = useSWR(
		isDialogOpen
			? `/integrations/whatsapp/${integratedAppId}/health-check`
			: null,
		() =>
			xhr
				.get<{
					data: TWAWebhookHealth
				}>(`/integrations/whatsapp/${integratedAppId}/health-check`)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	return (
		<>
			{children({ openDialog })}
			<Dialog
				open={isDialogOpen}
				onClose={closeDialog}
				title="WhatsApp Connection Health"
			>
				<Dialog.Body>
					{!data ? (
						error ? (
							<Alert status="error">
								{error?.message ||
									"Something went wrong. Please try after sometime."}
							</Alert>
						) : (
							<Spinner alignCenter padding="4" />
						)
					) : (
						<Stack gap="4">
							<Alert status={alertStatusForHealth(data.status)}>
								{data.status === "ok"
									? "Everything looks healthy. Messages and template updates from WhatsApp will reach Sembark."
									: data.status === "warning"
										? "Mostly healthy, but some items need your attention."
										: "Some events from WhatsApp are not reaching Sembark. See the failing checks below."}
							</Alert>
							<Stack gap="3">
								{data.checks.map((check: TWAWebhookHealthCheck) => (
									<WhatsAppHealthCheckItem key={check.key} check={check} />
								))}
							</Stack>
							<Text fontSize="sm" color="muted">
								Last checked <Time timestamp={data.checked_at} />
							</Text>
						</Stack>
					)}
				</Dialog.Body>
				<Dialog.Footer>
					<Inline gap="2">
						<Button onClick={() => recheck()} disabled={isValidating}>
							{isValidating ? "Checking..." : "Check Again"}
						</Button>
						<Button level="tertiary" onClick={closeDialog}>
							Close
						</Button>
					</Inline>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

function WhatsAppHealthCheckItem({ check }: { check: TWAWebhookHealthCheck }) {
	const accounts = businessAccountDetails(check)

	return (
		<Inline gap="2">
			<Box>
				<Badge
					success={check.status === "ok"}
					warning={check.status === "warning"}
					danger={check.status === "error"}
				>
					{check.status === "ok" ? <Icons.OkCircle /> : <Icons.Attention />}
				</Badge>
			</Box>
			<Stack gap="1" flex="1" minWidth="0">
				<Stack gap="px">
					<Text fontWeight="semibold">{check.label}</Text>
					<Text
						fontSize="sm"
						color={check.status === "error" ? "danger" : "muted"}
					>
						{check.message}
					</Text>
				</Stack>
				{accounts?.length ? (
					<Stack gap="1" paddingLeft="4" as="ul" listStyleType="disc">
						{accounts.map((account: TWAWebhookHealthAccount) => (
							<Box
								key={account.id}
								fontSize="sm"
								as="li"
								color={account.status === "error" ? "danger" : "muted"}
							>
								<Text>
									{account.name || account.wa_business_account_id}
									{": "}
									{account.message}
								</Text>
							</Box>
						))}
					</Stack>
				) : null}
			</Stack>
		</Inline>
	)
}

function alertStatusForHealth(
	status: TWAWebhookHealthStatus
): "success" | "warning" | "error" {
	return status === "ok" ? "success" : status
}

/**
 * The `business_accounts` check carries a row per account in its details.
 * Every other check carries something else, so narrow before rendering.
 */
function businessAccountDetails(
	check: TWAWebhookHealthCheck
): Array<TWAWebhookHealthAccount> | null {
	if (check.key !== "business_accounts" || !Array.isArray(check.details)) {
		return null
	}

	return check.details as Array<TWAWebhookHealthAccount>
}

export function SelectWABusinessAccount({
	integratedAppId,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	/**
	 * Optional filter — when present, narrows the listing to a specific
	 * integration. Omitted on inline create flows that haven't picked an
	 * integration yet (the chosen WABA itself uniquely determines it on
	 * submit).
	 */
	integratedAppId?: number
}) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			perFetchLimit={20}
			optionRenderer={({ option }: { option: TWhatsappBusinessAccount }) => (
				<Stack gap="1">
					<Text>{option.name}</Text>
					{option.wa_phone_numbers?.length ? (
						<Inline gap="1">
							{option.wa_phone_numbers.map(
								(ph: TWhatsappBusinessAccountPhoneNumber) => (
									<Badge warning={ph.status === "PENDING"} key={ph.id}>
										{ph.display_phone_number}
									</Badge>
								)
							)}
						</Inline>
					) : null}
				</Stack>
			)}
			{...props}
			fetch={async (q: string, { page }: { page: number }) => {
				const resp = await xhr.get("/wa-business-accounts", {
					params: {
						q,
						page,
						limit: 20,
						integration_id: integratedAppId,
					},
				})
				return resp.data.data as Array<TWhatsappBusinessAccount>
			}}
		/>
	)
}

/**
 * Tenant-wide WhatsApp phone-number async select. The three optional
 * filters narrow the listing on the server side; pick whichever fits the
 * caller's context:
 *
 * - `waTemplateId` — the automation create form (operator picks template
 *   first, sender phone is scoped to that template's WABA).
 * - `waBusinessAccountId` — the template editor (operator picks WABA
 *   first, sender phone is scoped to it).
 * - `integratedAppId` — broad scoping when the caller knows the
 *   integration but no WABA / template yet.
 *
 * Auto-disabled when no narrowing filter is provided (caller can override
 * with their own `disabled` prop).
 */
export function SelectWAPhoneNumber({
	integratedAppId,
	waBusinessAccountId,
	waTemplateId,
	allowAll,
	disabled,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	integratedAppId?: number
	waBusinessAccountId?: number
	waTemplateId?: number
	/**
	 * List every number of the tenant. Without a template, business account
	 * or integration to scope by, the picker is otherwise disabled — a
	 * template send must come from its own account's number, but a Flow has
	 * no such tie.
	 */
	allowAll?: boolean
}) {
	const xhr = useXHR()
	const hasFilter = Boolean(
		allowAll || waBusinessAccountId || waTemplateId || integratedAppId
	)

	return (
		<AsyncSelect
			perFetchLimit={20}
			{...props}
			disabled={disabled ?? !hasFilter}
			optionRenderer={({
				option,
			}: {
				option: TWhatsappBusinessAccountPhoneNumber
			}) => (
				<Stack gap="1">
					<Text>{option.name}</Text>
					<Text fontSize="sm" color="muted">
						{option.display_phone_number}
					</Text>
				</Stack>
			)}
			fetch={async (q: string, { page }: { page: number }) => {
				if (!hasFilter) return []
				const resp = await xhr.get("/wa-phone-numbers", {
					params: {
						q,
						page,
						limit: 20,
						wa_template_id: waTemplateId,
						wa_business_account_id: waBusinessAccountId,
						integration_id: integratedAppId,
					},
				})
				return resp.data.data as Array<TWhatsappBusinessAccountPhoneNumber>
			}}
		/>
	)
}

export type TWAFlow = {
	/** Ours, which is what an automation stores. */
	id: number
	/**
	 * Meta's own, shown so an operator can recognise the Flow. Absent on the
	 * stand-in a saved automation builds before the picker has loaded.
	 */
	wa_flow_id?: string
	name: string
	status: string
	categories?: Array<string>
	wa_business_account?: { id: number; name: string }
}

/**
 * A published WhatsApp Flow from the tenant's mirrored Flows. Narrow with
 * `waBusinessAccountId` when the sender is known. Flows are authored in
 * Meta's Flow Builder and brought here by the Flow sync.
 */
export function SelectWAFlow({
	waBusinessAccountId,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	waBusinessAccountId?: number
}) {
	const xhr = useXHR()

	return (
		<AsyncSelect
			{...props}
			labelKey="name"
			optionRenderer={({ option }: { option: TWAFlow }) => (
				<Stack gap="1">
					<Text>{option.name}</Text>
					<Text fontSize="sm" color="muted">
						{option.wa_flow_id ? `#${option.wa_flow_id}` : null}
						{option.wa_business_account
							? ` · ${option.wa_business_account.name}`
							: ""}
					</Text>
				</Stack>
			)}
			fetch={async (q: string) => {
				const resp = await xhr.get("/wa-flows", {
					params: {
						q,
						wa_business_account_id: waBusinessAccountId,
					},
				})
				return resp.data.data as Array<TWAFlow>
			}}
		/>
	)
}

export type TWAFlowScreen = {
	id: string
	title: string
}

/**
 * The screens of one Flow, read from its stored definition in the Flow's
 * own order. Name the business account when known. Meta requires the screen
 * a Flow opens on, so with `autoSelectFirst` the first screen is picked as
 * soon as the list arrives and nothing is picked yet. Remount (`key`) on a
 * Flow change so the list is re-read for the new Flow.
 */
export function SelectWAFlowScreen({
	flowId,
	waBusinessAccountId,
	autoSelectFirst,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch"> & {
	/** Our Flow id, as the picker stores it. */
	flowId?: string | number
	waBusinessAccountId?: number
	autoSelectFirst?: boolean
}) {
	const xhr = useXHR()
	const { value, onChange, name } = props

	return (
		<AsyncSelect
			{...props}
			labelKey="title"
			fetchOnMount={Boolean(flowId)}
			optionRenderer={({ option }: { option: TWAFlowScreen }) => (
				<Stack gap="1">
					<Text>{option.title}</Text>
					{option.title !== option.id ? (
						<Text fontSize="sm" color="muted">
							{option.id}
						</Text>
					) : null}
				</Stack>
			)}
			fetch={async (q: string) => {
				if (!flowId) return []
				const resp = await xhr.get(`/wa-flows/${flowId}/screens`, {
					params: { wa_business_account_id: waBusinessAccountId },
				})
				const screens = resp.data.data as Array<TWAFlowScreen>
				if (
					autoSelectFirst &&
					screens.length &&
					(!value || (Array.isArray(value) && !value.length))
				) {
					onChange?.(screens[0], name || "select")
				}
				const query = q.trim().toLowerCase()
				return query
					? screens.filter(
							(s) =>
								s.title.toLowerCase().includes(query) ||
								s.id.toLowerCase().includes(query)
						)
					: screens
			}}
		/>
	)
}

function getTemplateBodyPreview(template: TWATemplate): string {
	const bodyComponent = template.components?.find(
		(component) => component.type === "body"
	)

	return bodyComponent?.text || "Template message"
}

export function SelectWATemplate({
	waPhoneNumberId,
	waBusinessAccountId,
	sourceKey,
	includeSourceless,
	includeDrafts,
	unscoped,
	expectedSourceKey,
	automationTemplate,
	integratedAppId,
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch" | "onCreateNew"> & {
	waPhoneNumberId?: number
	waBusinessAccountId?: number
	sourceKey?: string | null
	/**
	 * The source the picked template will be sent for. Purely advisory: the
	 * listing still offers every template, and a template whose variables
	 * cannot be filled from this source is shown as such rather than hidden.
	 * The same rule is enforced when the automation is saved.
	 */
	expectedSourceKey?: string | null
	/**
	 * List every template, with no phone or source scope. A phone scope exists
	 * so a person cannot pick a template the chosen number is unable to send.
	 * A source scope is not a limit worth imposing here: which automations a
	 * template fits is decided by its variables, said on each option and
	 * enforced on save. Pass `expectedSourceKey` alongside to have it said.
	 */
	unscoped?: boolean
	/**
	 * Also offer templates bound to no automation. They need no source, so a
	 * person sending one types the values — which an automation cannot, so this
	 * is off unless a manual send asks for it.
	 */
	includeSourceless?: boolean
	/**
	 * When true, the listing also includes non-rejected drafts/pending
	 * templates. The automation create flow uses this so a freshly created
	 * (still PENDING) template is selectable immediately.
	 */
	includeDrafts?: boolean
	/**
	 * Inline-create context — passed to the editor dialog when the operator
	 * picks "Create new template". When provided, the editor pre-fills from
	 * the automation template's defaults and pre-selects the linkage.
	 */
	automationTemplate?: TWhatsAppAutomationTemplate | null
	/**
	 * Optional integration scope for the inline-create dialog. When omitted
	 * (e.g. on the global automation page), the editor's WABA picker stays
	 * tenant-wide.
	 */
	integratedAppId?: number
}) {
	const xhr = useXHR()
	const isEnabled = Boolean(
		unscoped || waPhoneNumberId || waBusinessAccountId || sourceKey
	)

	// Inline create-template flow: when `creatable` is set on the underlying
	// AsyncSelect, we wire `onCreateNew` to open the create dialog and
	// resolve the Select's internal promise with the saved template (or
	// undefined when the operator cancels). Callers don't need to wire any
	// of this themselves.
	const [createOpen, setCreateOpen] = useState(false)
	const promiseRef = useRef<((t: TWATemplate | undefined) => void) | null>(null)
	const handleCreateNew = useCallback(() => {
		return new Promise<TWATemplate | undefined>((resolve) => {
			promiseRef.current = resolve
			setCreateOpen(true)
		})
	}, [])
	const handleCreated = useCallback((t: TWATemplate) => {
		setCreateOpen(false)
		const resolve = promiseRef.current
		promiseRef.current = null
		resolve?.(t)
	}, [])
	const handleCancel = useCallback(() => {
		setCreateOpen(false)
		const resolve = promiseRef.current
		promiseRef.current = null
		resolve?.(undefined)
	}, [])

	return (
		<>
			<AsyncSelect
				{...props}
				onCreateNew={handleCreateNew}
				labelKey="name"
				perFetchLimit={20}
				disabled={!isEnabled}
				menuPlaceholder={
					isEnabled
						? includeDrafts
							? "Search templates"
							: "Search approved templates"
						: "Select a source or WhatsApp phone number first"
				}
				cacheKey={(() => {
					const sp = new URLSearchParams()
					if (waPhoneNumberId)
						sp.set("wa_phone_number_id", String(waPhoneNumberId))
					if (waBusinessAccountId)
						sp.set("wa_business_account_id", String(waBusinessAccountId))
					if (sourceKey) sp.set("parameters_source_key", sourceKey)
					if (includeSourceless) sp.set("include_sourceless", "1")
					sp.set(
						"status_in",
						includeDrafts ? "APPROVED,PENDING,DRAFT" : "APPROVED"
					)
					const qs = sp.toString()
					return qs ? `/wa-message-templates?${qs}` : undefined
				})()}
				optionRenderer={({
					option,
					created,
				}: {
					option: TWATemplate
					created?: boolean
				}) => {
					if (created) {
						return <Text>Create New Template</Text>
					}

					const mismatch = waTemplateSourceMismatch(option, expectedSourceKey)

					return (
						<Stack gap="1">
							<Inline gap="2" alignItems="center" flexWrap="wrap">
								<Text fontWeight="semibold">{option.name}</Text>
								<Badge>{option.language.toUpperCase()}</Badge>
								<Badge outlined>{option.category}</Badge>
								<Badge
									success={option.status === "APPROVED"}
									warning={option.status === "PENDING"}
									danger={option.status === "REJECTED"}
								>
									{option.status}
								</Badge>
								{option.template_data_source_name ? (
									<Badge outlined>{option.template_data_source_name}</Badge>
								) : null}
							</Inline>
							<Text fontSize="sm" color="muted">
								{getTemplateBodyPreview(option)}
							</Text>
							{/* Said here rather than hidden from the listing: the
							    operator can see the template exists and why this
							    automation cannot send it. */}
							{mismatch ? (
								<Text fontSize="sm" color="warning">
									{mismatch} — this automation can't fill them.
								</Text>
							) : null}
						</Stack>
					)
				}}
				fetch={async (q: string, { page }: { page: number }) => {
					if (!isEnabled) {
						return []
					}

					return xhr
						.get<IListResponse<TWATemplate>>(`/wa-message-templates`, {
							params: {
								q,
								page,
								limit: 20,
								pagination: "simple",
								wa_phone_number_id: waPhoneNumberId,
								wa_business_account_id: waBusinessAccountId,
								parameters_source_key: sourceKey,
								include_sourceless: includeSourceless ? 1 : undefined,
								status_in: includeDrafts
									? "APPROVED,PENDING,DRAFT"
									: "APPROVED",
							},
						})
						.then((resp) => resp.data.data)
				}}
			/>
			{createOpen ? (
				<CreateWATemplateInDialog
					open
					integratedAppId={integratedAppId}
					automationTemplate={automationTemplate}
					onClose={handleCancel}
					onSuccess={handleCreated}
				/>
			) : null}
		</>
	)
}

type TPreviewMode = "raw" | "sample"

const WHATSAPP_FONT_FAMILY =
	"SF Pro Text,SF Pro Icons,system,-apple-system,system-ui,BlinkMacSystemFont,Helvetica Neue,Helvetica,Lucida Grande,Kohinoor Devanagari,sans-serif"

type TWATemplatePreviewBubbleProps = {
	template: Pick<TWATemplate, "id">
	mode: TPreviewMode
} & (
	| { sourceKey?: undefined; sourceRecordId?: undefined }
	/**
	 * Fill the preview from this record, as it would send, instead of showing
	 * `mode`'s placeholders or samples -- and say what it leaves blank. The
	 * record's kind and its id, always together.
	 */
	| { sourceKey: string; sourceRecordId: number | string }
)

export function WATemplatePreviewBubble({
	template,
	mode,
	sourceKey,
	sourceRecordId,
}: TWATemplatePreviewBubbleProps) {
	const {
		data,
		error,
		isValidating: loading,
	} = useFetchWATemplatePreview(template.id, mode, sourceKey, sourceRecordId)
	const html = data?.html
	const missingParameterNames = data?.missingParameterNames || []
	const errorMessage = error
		? error instanceof Error
			? error.message
			: "Preview failed"
		: null

	return (
		<Box
			style={{
				background: "#faf7eb",
				color: "black",
				fontFamily: WHATSAPP_FONT_FAMILY,
			}}
			paddingY="4"
			paddingX="4"
			rounded="lg"
		>
			<Box maxWidth="md" position="relative">
				{errorMessage ? (
					<Box
						paddingY="3"
						paddingX="3"
						rounded="md"
						style={{
							background: "#fef2f2",
							border: "1px solid #fecaca",
							color: "#991b1b",
						}}
					>
						<Text fontSize="sm">{errorMessage}</Text>
					</Box>
				) : html ? (
					<Box dangerouslySetInnerHTML={{ __html: html }} />
				) : loading ? (
					<Text color="muted" fontSize="sm">
						Loading preview…
					</Text>
				) : null}
				{loading && html ? (
					<Box
						position="absolute"
						top="0"
						right="0"
						paddingX="2"
						paddingY="px"
						rounded="md"
						style={{ background: "rgba(255,255,255,0.7)" }}
					>
						<Text fontSize="xs" color="muted">
							Updating…
						</Text>
					</Box>
				) : null}
			</Box>
			{missingParameterNames.length ? (
				<Box marginTop="3">
					<Alert status="warning" inline>
						<Text fontSize="sm">
							This record cannot fill {missingParameterNames.join(", ")} yet, so
							the message cannot be sent as it stands.
						</Text>
					</Alert>
				</Box>
			) : null}
		</Box>
	)
}

function WATemplateDetails({
	template,
}: {
	template: TWATemplate
	integratedAppId: number
}) {
	const [mode, setMode] = useState<"raw" | "sample" | "json">("raw")
	const phoneNumber = template.phone_number
	const automationKey = template.automation_template_key
	return (
		<Stack gap="6">
			<Stack gap="4">
				<Stack gap="1">
					<Text fontSize="sm" color="muted">
						Name
					</Text>
					<Text fontWeight="semibold">{template.name}</Text>
				</Stack>
				<Inline gap="2" flexWrap="wrap">
					<Badge
						success={template.status === "APPROVED"}
						warning={template.status === "PENDING"}
						danger={template.status === "REJECTED"}
					>
						{template.status}
					</Badge>
					<Badge
						success={template.category === "UTILITY"}
						warning={template.category === "MARKETING"}
					>
						{template.category}
					</Badge>
					<Badge>{template.language.toUpperCase()}</Badge>
					{template.parameter_format ? (
						<Badge>{template.parameter_format}</Badge>
					) : null}
				</Inline>
			</Stack>
			<Inline gap="4" flexWrap="wrap">
				<Stack gap="1">
					<Text fontSize="sm" color="muted">
						Business Account
					</Text>
					<Text fontWeight="semibold">{template.wa_business_account.name}</Text>
					<Text fontSize="xs" color="muted">
						WABA: {template.wa_business_account.wa_business_account_id}
					</Text>
				</Stack>
				{template.wa_template_id ? (
					<Stack gap="1">
						<Text fontSize="sm" color="muted">
							Meta Template ID
						</Text>
						<Text fontSize="sm" fontFamily="mono">
							{template.wa_template_id}
						</Text>
					</Stack>
				) : null}
				<Stack gap="1">
					<Text fontSize="sm" color="muted">
						Default Sender Phone
					</Text>
					{phoneNumber ? (
						<Stack gap="px">
							<Text fontWeight="semibold">
								{phoneNumber.display_phone_number}
							</Text>
							<Text fontSize="xs" color="muted">
								{phoneNumber.wa_phone_number_id}
							</Text>
						</Stack>
					) : (
						<Text fontSize="sm" color="muted">
							Not linked
						</Text>
					)}
				</Stack>
				<Stack gap="1">
					<Text fontSize="sm" color="muted">
						Linked Automation
					</Text>
					{automationKey ? (
						<Text fontSize="sm" fontFamily="mono">
							{automationKey}
						</Text>
					) : (
						<Text fontSize="sm" color="muted">
							Not linked
						</Text>
					)}
				</Stack>
				{template.message_send_ttl_seconds ? (
					<Stack gap="1">
						<Text fontSize="sm" color="muted">
							Message TTL
						</Text>
						<Text fontSize="sm">
							{template.message_send_ttl_seconds} seconds
						</Text>
					</Stack>
				) : null}
				<Stack gap="1">
					<Text fontSize="sm" color="muted">
						Created
					</Text>
					<Time
						fontSize="sm"
						timestamp={template.created_at}
						dateFormat
						timeFormat
					/>
				</Stack>
			</Inline>

			<Stack gap="2">
				<Inline gap="2" alignItems="center" justifyContent="between">
					<Text fontWeight="semibold">Preview</Text>
					<Inline gap="1">
						<Button
							size="sm"
							level={mode === "raw" ? "primary" : "tertiary"}
							onClick={() => setMode("raw")}
						>
							Variables
						</Button>
						<Button
							size="sm"
							level={mode === "sample" ? "primary" : "tertiary"}
							onClick={() => setMode("sample")}
						>
							Sample values
						</Button>
						<Button
							size="sm"
							level={mode === "json" ? "primary" : "tertiary"}
							onClick={() => setMode("json")}
						>
							JSON
						</Button>
					</Inline>
				</Inline>
				{mode === "json" ? (
					<JSONPreview data={template.components} />
				) : (
					<WATemplatePreviewBubble template={template} mode={mode} />
				)}
			</Stack>
		</Stack>
	)
}

/**
 * Rendered by Search inside its own form. Reads the integration from the
 * WhatsApp page's outlet context, so the account picker lists only this
 * integration's accounts.
 */
function WhatsAppTemplatesFilters() {
	const { appId } = useOutletContext<{ appId: number }>()
	return (
		<Stack gap="4">
			<SelectField
				select={SelectWABusinessAccount}
				name="wa_business_account"
				label="Business Account"
				integratedAppId={appId}
			/>
		</Stack>
	)
}

export function WhatsAppTemplatesList({
	integrationId,
	integratedAppId,
}: {
	integrationId: number
	integratedAppId: number
}) {
	const [freshKey, refreshKey] = useFreshId()
	// The account filter is mirrored into the URL, so the Accounts tab can
	// link straight to one account's templates.
	const [query, setQuery] = useLocationQuery<
		ITemplateFilters,
		ITemplateFiltersInLocationQuery
	>({
		toQuery: templateFiltersToLocationQuery,
		fromQuery: locationQueryToTemplateFilters,
	})
	const [searchParams, setSearchParams] = useSearch<ITemplateFilters>(query)

	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])

	const xhr = useXHR()

	return (
		<Search<ITemplateFilters>
			title="Message Templates"
			initialParams={searchParams}
			Filters={WhatsAppTemplatesFilters}
			onSearch={(params) => {
				setSearchParams({ ...params, page: 1 })
			}}
			actions={
				<Inline gap="2">
					<ButtonLink
						level="primary"
						to={generatePath(
							"/integrations/:integrationId/whatsapp/templates/new",
							{ integrationId: String(integrationId) }
						)}
					>
						<Icons.Plus /> Create Template
					</ButtonLink>
					<Component initialState={false}>
						{({ state, setState }) => (
							<>
								<Dropdown alignRight>
									<Dropdown.ToggleButton>
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Dropdown.MenuItem
											onClick={(e) => {
												e.preventDefault()
												setState(true)
											}}
										>
											<Icons.Refresh /> Sync Templates
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>
								<Dialog
									open={state}
									onClose={() => setState(false)}
									title="Sync Templates from Meta Business Accounts"
									sm
								>
									<Dialog.Body>
										<Form
											initialValues={{ app_id: integratedAppId }}
											onSubmit={withServerErrors(
												async (values: { app_id: number }) => {
													await xhr.post(
														`/integrations/whatsapp/${values.app_id}/sync-message-templates`
													)
													showSnackbar(
														"Message templates will be updated shortly. Please check back in sometime."
													)
													setState(false)
												}
											)}
										>
											{({ submitting, handleSubmit }) => (
												<form onSubmit={handleSubmit} noValidate>
													<Stack gap="4">
														<Text>
															Do you want to fetch templates from the Business
															accounts? System will fetch the templates from the
															Meta.
														</Text>
														<SubmissionError />
														<Inline gap="4">
															<Button type="submit" disabled={submitting}>
																{submitting
																	? "Starting Process"
																	: "Start Syncing"}
															</Button>
															<Button
																onClick={() => setState(false)}
																disabled={submitting}
															>
																Cancel
															</Button>
														</Inline>
													</Stack>
												</form>
											)}
										</Form>
									</Dialog.Body>
								</Dialog>
							</>
						)}
					</Component>
				</Inline>
			}
		>
			{() => (
				<ListView<TWATemplate, ITemplateFilters>
					pageKey={`integrations/${integrationId}/whatsapp/${integratedAppId}/templates-${freshKey}`}
					params={searchParams}
					onPageChange={(page: number) =>
						setSearchParams({ ...searchParams, page })
					}
					fetch={async (xhr, params) => {
						return xhr
							.get(`/wa-message-templates`, {
								params: templateFiltersToRequestParams(params, integratedAppId),
							})
							.then((resp) => resp.data)
					}}
				>
					{({ items }: { items: TWATemplate[] }) => (
						<>
							{items.length === 0 ? (
								<Alert status="info">
									<Stack gap="2">
										<Text fontWeight="semibold">No Templates Found</Text>
										<Text>
											You haven't created any message templates yet. Create
											whatsapp templates to get started.
										</Text>
									</Stack>
								</Alert>
							) : (
								<Table
									bordered
									striped
									responsive
									headers={[
										"Template Name",
										"Language",
										"Category",
										"Status",
										"",
									]}
									rows={items.map((template: TWATemplate) => [
										<Component initialState={false}>
											{({ state, setState }) => (
												<>
													<Stack gap="1">
														<Box>
															<Button inline onClick={() => setState(true)}>
																<Text
																	fontWeight="semibold"
																	as="span"
																	color="accent"
																>
																	{template.name}
																</Text>
															</Button>
														</Box>
														{template.automation_template ? (
															<Inline
																gap="1"
																alignItems="center"
																flexWrap="wrap"
															>
																<Badge success>Linked</Badge>
																<Text fontSize="sm" color="muted">
																	{template.automation_template.name}
																</Text>
															</Inline>
														) : null}
														{template.components?.[0]?.text ? (
															<Text fontSize="sm" color="muted">
																{template.components?.[0]?.text?.substring(
																	0,
																	100
																)}
																{Number(
																	template.components?.[0]?.text?.length
																) > 100
																	? "..."
																	: ""}
															</Text>
														) : null}
													</Stack>
													<Dialog
														open={state}
														title="Template Details"
														onClose={() => setState(false)}
														lg
													>
														<Dialog.Body>
															<WATemplateDetails
																template={template}
																integratedAppId={integratedAppId}
															/>
														</Dialog.Body>
														<Dialog.Footer>
															<Inline
																gap="2"
																justifyContent="between"
																width="full"
																flexWrap="wrap"
															>
																<Inline gap="2" flexWrap="wrap">
																	<ButtonLink
																		size="sm"
																		level="secondary"
																		to={generatePath(
																			"/integrations/:integrationId/whatsapp/templates/:templateId/edit",
																			{
																				integrationId: String(integrationId),
																				templateId: String(template.id),
																			}
																		)}
																	>
																		Edit
																	</ButtonLink>
																	<EditWATemplateLinksInDialog
																		template={template}
																		integratedAppId={integratedAppId}
																		onSuccess={() => {
																			refreshKey()
																		}}
																	>
																		{({ openDialog }) => (
																			<Button
																				size="sm"
																				level="secondary"
																				onClick={openDialog}
																			>
																				Link
																			</Button>
																		)}
																	</EditWATemplateLinksInDialog>
																	{template.status === "APPROVED" ? (
																		<SendTestWAMessageInDialog
																			waTemplate={template}
																			integratedAppId={integratedAppId}
																		>
																			{({ openDialog }) => (
																				<Button
																					size="sm"
																					level="secondary"
																					onClick={openDialog}
																				>
																					Test Message
																				</Button>
																			)}
																		</SendTestWAMessageInDialog>
																	) : null}
																</Inline>
																<Button onClick={() => setState(false)}>
																	Close
																</Button>
															</Inline>
														</Dialog.Footer>
													</Dialog>
												</>
											)}
										</Component>,
										<Text>{template.language.toUpperCase()}</Text>,
										<Badge
											success={template.category === "UTILITY"}
											warning={template.category === "MARKETING"}
										>
											{template.category}
										</Badge>,
										<Badge
											success={template.status === "APPROVED"}
											warning={template.status === "PENDING"}
											danger={template.status === "REJECTED"}
										>
											{template.status}
										</Badge>,
										<Inline gap="2" justifyContent="end">
											{!template.automation_template ? (
												<EditWATemplateLinksInDialog
													template={template}
													integratedAppId={integratedAppId}
													onSuccess={() => {
														refreshKey()
													}}
												>
													{({ openDialog }) => (
														<Button
															size="sm"
															level="secondary"
															onClick={openDialog}
														>
															Link
														</Button>
													)}
												</EditWATemplateLinksInDialog>
											) : null}
											<SendTestWAMessageInDialog
												waTemplate={template}
												integratedAppId={integratedAppId}
											>
												{({ openDialog: openSendTestMessageDialog }) => (
													<Dropdown alignRight>
														<Dropdown.ToggleButton size="sm">
															<Icons.DotsVertical />
														</Dropdown.ToggleButton>
														<Dropdown.Menu>
															<Dropdown.MenuItem
																as={Link}
																to={
																	"/integrations/:integrationId/whatsapp/templates/:templateId/edit"
																}
																params={{
																	integrationId: String(integrationId),
																	templateId: String(template.id),
																}}
															>
																Edit Template
															</Dropdown.MenuItem>
															{template.status === "APPROVED" ? (
																<Dropdown.MenuItem
																	onClick={(e) => {
																		e.preventDefault()
																		openSendTestMessageDialog()
																	}}
																>
																	Test Message
																</Dropdown.MenuItem>
															) : null}
														</Dropdown.Menu>
													</Dropdown>
												)}
											</SendTestWAMessageInDialog>
										</Inline>,
									])}
								/>
							)}
						</>
					)}
				</ListView>
			)}
		</Search>
	)
}

function WhatsAppFlowsFilters() {
	const { appId } = useOutletContext<{ appId: number }>()
	return (
		<Stack gap="4">
			<SelectField
				select={SelectWABusinessAccount}
				name="waba"
				label="Business Account"
				integratedAppId={appId}
			/>
			<SelectInputField name="status" label="Status">
				<option value="">Any status</option>
				<option value="PUBLISHED">Published</option>
				<option value="DRAFT">Draft</option>
				<option value="DEPRECATED">Deprecated</option>
			</SelectInputField>
		</Stack>
	)
}

/**
 * Flows authored in Meta's Flow Builder are mirrored here by the sync; a
 * Flow created here from its JSON is listed at once, as a draft.
 */
export function WhatsAppFlowsList({
	integrationId,
	integratedAppId,
}: {
	integrationId: number
	integratedAppId: number
}) {
	const [freshKey, refreshKey] = useFreshId()
	const [query, setQuery] = useLocationQuery<
		IFlowFilters,
		IFlowFiltersInLocationQuery
	>({
		toQuery: flowFiltersToLocationQuery,
		fromQuery: locationQueryToFlowFilters,
	})
	const [searchParams, setSearchParams] = useSearch<IFlowFilters>(query)

	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])

	return (
		<Search<IFlowFilters>
			title="Flows"
			initialParams={searchParams}
			Filters={WhatsAppFlowsFilters}
			onSearch={(params) => {
				setSearchParams({ ...params, page: 1 })
			}}
			actions={
				<Inline gap="2">
					<ButtonLink
						level="primary"
						to={generatePath(
							"/integrations/:integrationId/whatsapp/flows/new",
							{ integrationId: String(integrationId) }
						)}
					>
						<Icons.Plus /> Create Flow
					</ButtonLink>
					<SyncWAFlowsMenu
						integratedAppId={integratedAppId}
						onQueued={() => refreshKey()}
					/>
				</Inline>
			}
		>
			{() => (
				<ListView<TWAFlowRecord, IFlowFilters>
					pageKey={`integrations/${integrationId}/whatsapp/${integratedAppId}/flows-${freshKey}`}
					params={searchParams}
					onPageChange={(page: number) =>
						setSearchParams({ ...searchParams, page })
					}
					fetch={async (xhr, params) => {
						return xhr
							.get(`/integrations/whatsapp/${integratedAppId}/flows`, {
								params: flowFiltersToRequestParams(params),
							})
							.then((resp) => resp.data)
					}}
				>
					{({ items }: { items: TWAFlowRecord[] }) => (
						<>
							{items.length === 0 ? (
								<Alert status="info">
									<Stack gap="2">
										<Text fontWeight="semibold">No Flows Found</Text>
										<Text>
											Create a Flow here from its JSON, or build one in Meta's
											Flow Builder and sync to bring it here so automations can
											offer it.
										</Text>
									</Stack>
								</Alert>
							) : (
								<Table
									bordered
									striped
									responsive
									headers={[
										"Flow Name",
										"Meta Id",
										"Status",
										"Business Account",
										"Last Synced",
										"",
									]}
									rows={items.map((flow: TWAFlowRecord) => [
										<WAFlowJSONDialog flow={flow}>
											{({ preview }) => (
												<Stack gap="1">
													<Box>
														<Button inline onClick={preview}>
															<Text
																fontWeight="semibold"
																as="span"
																color="accent"
															>
																{flow.name}
															</Text>
														</Button>
													</Box>
													{flow.categories.length ? (
														<Text fontSize="sm" color="muted">
															{flow.categories.join(", ")}
														</Text>
													) : null}
												</Stack>
											)}
										</WAFlowJSONDialog>,
										<Text fontSize="sm" fontFamily="mono">
											{flow.wa_flow_id}
										</Text>,
										<Badge
											success={flow.status === "PUBLISHED"}
											warning={flow.status === "DRAFT"}
										>
											{flow.status}
										</Badge>,
										<Text>{flow.wa_business_account?.name || "—"}</Text>,
										<Text fontSize="sm" color="muted">
											{flow.definition_synced_at ? (
												<Time
													timestamp={flow.definition_synced_at}
													dateFormat
													timeFormat
												/>
											) : (
												"Not read yet"
											)}
										</Text>,
										<WAFlowRowActions
											flow={flow}
											integrationId={integrationId}
											onChanged={refreshKey}
										/>,
									])}
								/>
							)}
						</>
					)}
				</ListView>
			)}
		</Search>
	)
}

/**
 * The Flow's JSON is not in the listing — it runs to kilobytes — so it is
 * fetched when the dialog opens.
 */
function WAFlowJSONDialog({
	flow,
	children,
}: {
	flow: TWAFlowRecord
	children: (props: { preview: () => void }) => React.ReactNode
}) {
	const [isOpen, setIsOpen] = useState(false)
	const xhr = useXHR()
	const { data, error } = useSWR(
		isOpen ? `/wa-flows/${flow.id}` : null,
		() =>
			xhr
				.get<{ data: TWAFlowRecord }>(`/wa-flows/${flow.id}`)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)
	return (
		<>
			{children({ preview: () => setIsOpen(true) })}
			<Dialog
				open={isOpen}
				onClose={() => setIsOpen(false)}
				title={flow.name}
				lg
			>
				<Dialog.Body>
					{error ? (
						<Alert status="error">
							{(error as Error)?.message ||
								"Something went wrong. Please try after sometime."}
						</Alert>
					) : !data ? (
						<Spinner alignCenter padding="4" />
					) : data.definition ? (
						<Stack gap="4">
							<WAFlowSubmittedFields fields={data.submitted_fields || []} />
							<JSONPreview data={data.definition} />
						</Stack>
					) : (
						<Alert status="info">
							This Flow's JSON has not been read yet. Sync your Flows to bring
							it here.
						</Alert>
					)}
				</Dialog.Body>
				<Dialog.Footer>
					<Button onClick={() => setIsOpen(false)}>Close</Button>
				</Dialog.Footer>
			</Dialog>
		</>
	)
}

/**
 * Preview any Flow on Meta, edit a draft or a published one, publish a
 * draft. A published Flow's name and categories are fixed, so publishing
 * asks first.
 */
function WAFlowRowActions({
	flow,
	integrationId,
	onChanged,
}: {
	flow: TWAFlowRecord
	integrationId: number
	onChanged: () => void
}) {
	const xhr = useXHR()
	const [isPreviewOpen, setIsPreviewOpen] = useState(false)
	const [isPublishing, setIsPublishing] = useState(false)

	const publish = async () => {
		if (
			!window.confirm(
				`Publish "${flow.name}"? A published Flow can be offered to customers, but its name and categories can no longer be changed.`
			)
		) {
			return
		}
		setIsPublishing(true)
		try {
			await xhr.post(`/wa-flows/${flow.id}/publish`)
			showSnackbar(`"${flow.name}" is published.`)
			onChanged()
		} catch (error) {
			showSnackbar((error as Error)?.message || "Could not publish the Flow.")
		} finally {
			setIsPublishing(false)
		}
	}

	return (
		<>
			<Inline gap="2" justifyContent="end">
				<Button size="sm" onClick={() => setIsPreviewOpen(true)}>
					<Icons.Eye /> Preview
				</Button>
				{flow.status === "DRAFT" || flow.status === "PUBLISHED" ? (
					<ButtonLink
						size="sm"
						to={generatePath(
							"/integrations/:integrationId/whatsapp/flows/:flowId/edit",
							{
								integrationId: String(integrationId),
								flowId: String(flow.id),
							}
						)}
					>
						<Icons.Pencil /> Edit
					</ButtonLink>
				) : null}
				{flow.status === "DRAFT" ? (
					<>
						<Button
							size="sm"
							status="primary"
							onClick={publish}
							disabled={isPublishing}
						>
							<Icons.Upload /> {isPublishing ? "Publishing..." : "Publish"}
						</Button>
					</>
				) : null}
			</Inline>
			{isPreviewOpen ? (
				<WAFlowPreviewDialog
					flow={flow}
					onClose={() => setIsPreviewOpen(false)}
				/>
			) : null}
		</>
	)
}

/**
 * Meta's preview link expires, so it is asked for when the dialog opens.
 * The link is then opened by the operator's own click, which a browser
 * lets through where a window opened after the request would be blocked.
 */
function WAFlowPreviewDialog({
	flow,
	onClose,
}: {
	flow: TWAFlowRecord
	onClose: () => void
}) {
	const xhr = useXHR()
	const { data: previewUrl, error } = useSWR(
		`/wa-flows/${flow.id}/preview`,
		(url: string) =>
			xhr
				.get<{ data: { preview_url: string } }>(url)
				.then((resp) => resp.data.data.preview_url),
		{ revalidateOnFocus: false }
	)

	return (
		<Dialog open onClose={onClose} title={`Preview ${flow.name}`}>
			<Dialog.Body>
				{error ? (
					<Alert status="error">
						{(error as Error)?.message ||
							"Unable to get the preview link from Meta."}
					</Alert>
				) : !previewUrl ? (
					<Spinner alignCenter padding="4" />
				) : (
					<Stack gap="4">
						<Text>
							See the Flow the way a customer would, on Meta's preview page. The
							link expires after a while.
						</Text>
						<Box>
							<Button
								as="a"
								href={previewUrl}
								target="_blank"
								rel="noreferrer noopener"
								level="primary"
							>
								Open Preview <Icons.ArrowTopRightOnSquare />
							</Button>
						</Box>
					</Stack>
				)}
			</Dialog.Body>
			<Dialog.Footer>
				<Button onClick={onClose}>Close</Button>
			</Dialog.Footer>
		</Dialog>
	)
}

/**
 * The keys each screen sends back when the customer finishes the Flow —
 * the only answers we ever receive — and why nothing would be sent.
 */
function WAFlowSubmittedFields({
	fields,
	errors = [],
}: {
	fields: Array<TWAFlowSubmittedField>
	errors?: Array<string>
}) {
	if (errors.length) {
		return (
			<Alert status="error" title="This Flow would not send us its answers">
				<Stack as="ul" gap="1">
					{errors.map((error) => (
						<li key={error}>{error}</li>
					))}
				</Stack>
			</Alert>
		)
	}

	if (!fields.length) {
		return (
			<Alert status="warning">
				This Flow sends no answers back when finished.
			</Alert>
		)
	}

	const screens = Array.from(new Set(fields.map((field) => field.screen)))

	return (
		<Stack gap="2">
			<Text fontWeight="semibold">Data sent on submit</Text>
			{screens.map((screen) => (
				<Inline gap="2" key={screen} alignItems="center">
					<Text fontSize="sm" color="muted">
						{screen}:
					</Text>
					{fields
						.filter((field) => field.screen === screen)
						.map((field) => (
							<Badge key={field.key}>
								<Text as="span" fontFamily="mono">
									{field.key}
								</Text>
							</Badge>
						))}
				</Inline>
			))}
		</Stack>
	)
}

/**
 * Asks the server what the JSON being written would send back, once the
 * operator pauses typing.
 */
function WAFlowJSONInspector({ flowJson }: { flowJson: string }) {
	const xhr = useXHR()
	const [debouncedJson, setDebouncedJson] = useState(flowJson)

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedJson(flowJson), 500)
		return () => clearTimeout(timer)
	}, [flowJson])

	// JSON that does not parse is already flagged by the field's own
	// validation; only a parsable object is worth asking the server about.
	const isParsable = isJSONObject(debouncedJson)

	const { data } = useSWR(
		isParsable ? ["/wa-flows/inspect", debouncedJson] : null,
		([url, json]: [string, string]) =>
			xhr
				.post<{
					fields: Array<TWAFlowSubmittedField>
					errors: Array<string>
				}>(url, { flow_json: json })
				.then((resp) => resp.data),
		{ revalidateOnFocus: false, keepPreviousData: true }
	)

	if (!isParsable || !data) return null

	return <WAFlowSubmittedFields fields={data.fields} errors={data.errors} />
}

type TWAFlowCategoryOption = { id: string; name: string }

const waFlowCategoryOptions: Array<TWAFlowCategoryOption> =
	WA_FLOW_CATEGORIES.map((category) => ({
		id: category,
		name: category.replace(/_/g, " "),
	}))

type TWAFlowPresetOption = { id: string; name: string }

interface IWAFlowFormValues {
	wa_business_account?: Pick<TWhatsappBusinessAccount, "id" | "name"> | null
	name: string
	categories: Array<TWAFlowCategoryOption>
	flow_json: string
	/** Only drive the preset picker; never submitted. */
	preset?: TWAFlowPresetOption | null
	preset_fields?: Array<TWAFlowPresetOption>
}

/**
 * Start from a ready-made Flow: pick a preset and its fields, and the
 * built Flow JSON replaces the JSON field. The name and categories are
 * filled too, unless the operator has set them already.
 */
function WAFlowPresetPicker() {
	const xhr = useXHR()
	const form = useForm<IWAFlowFormValues>()
	const [isBuilding, setIsBuilding] = useState(false)
	const [buildError, setBuildError] = useState<string>()
	const { data: presets, error } = useSWR(
		"/wa-flows/presets",
		(url: string) =>
			xhr
				.get<{ data: Array<TWAFlowPreset> }>(url)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	if (error) {
		return (
			<Alert status="error">
				{(error as Error)?.message || "Unable to load the Flow presets."}
			</Alert>
		)
	}

	if (!presets) {
		return <Spinner alignCenter padding="4" />
	}

	const findPreset = (option?: TWAFlowPresetOption | null) =>
		presets.find((preset) => preset.key === option?.id)

	const fieldOptions = (preset: TWAFlowPreset): Array<TWAFlowPresetOption> =>
		preset.fields.map((field) => ({
			id: field.key,
			name: field.required ? `${field.label} (required)` : field.label,
		}))

	const buildFlowJSON = async () => {
		const { values } = form.getState()
		const preset = findPreset(values.preset)
		if (!preset) return

		if (
			values.flow_json.trim() &&
			!window.confirm("This replaces the Flow JSON you have. Continue?")
		) {
			return
		}

		setIsBuilding(true)
		setBuildError(undefined)
		try {
			const { data } = await xhr.post<{
				data: {
					name: string
					categories: Array<string>
					definition: Record<string, unknown>
				}
			}>(`/wa-flows/presets/${preset.key}`, {
				fields: (values.preset_fields || []).map((field) => field.id),
			})

			form.batch(() => {
				form.change("flow_json", JSON.stringify(data.data.definition, null, 2))
				if (!values.name.trim()) {
					form.change("name", data.data.name)
				}
				if (!values.categories?.length) {
					form.change(
						"categories",
						waFlowCategoryOptions.filter((option) =>
							data.data.categories.includes(option.id)
						)
					)
				}
			})
		} catch (e) {
			setBuildError((e as Error)?.message || "Unable to build the Flow.")
		} finally {
			setIsBuilding(false)
		}
	}

	return (
		<Stack gap="4">
			<SelectField
				select={Select}
				name="preset"
				label="Preset"
				options={presets.map((preset) => ({
					id: preset.key,
					name: preset.name,
				}))}
				placeholder="Select a preset"
				maxWidth="sm"
				onChange={(option: TWAFlowPresetOption | null) => {
					const preset = findPreset(option)
					form.batch(() => {
						form.change("preset", option)
						form.change(
							"preset_fields",
							preset
								? fieldOptions(preset).filter(
										(_, index) => preset.fields[index].default
									)
								: []
						)
					})
				}}
			/>
			<GetFieldValue<IWAFlowFormValues["preset"]> name="preset">
				{({ value }) => {
					const preset = findPreset(value)
					if (!preset) return null
					return (
						<Stack gap="4">
							<Text color="muted">{preset.description}</Text>
							<SelectField
								select={Select}
								name="preset_fields"
								label="Fields"
								options={fieldOptions(preset)}
								multiple
								help="Required fields are always included."
								fullWidth
							/>
							{buildError ? <Alert status="error">{buildError}</Alert> : null}
							<Box>
								<Button onClick={buildFlowJSON} disabled={isBuilding}>
									{isBuilding ? "Building..." : "Fill Flow JSON"}
								</Button>
							</Box>
						</Stack>
					)
				}}
			</GetFieldValue>
		</Stack>
	)
}

function isJSONObject(value: string): boolean {
	if (!value.trim()) return false
	try {
		const parsed = JSON.parse(value)
		return (
			typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
		)
	} catch {
		return false
	}
}

/**
 * Pretty-prints the Flow JSON with 2-space indentation. JSON that does not
 * parse is left as typed, so the validation message can point at it.
 */
function formatFlowJSON(value: string): string {
	if (!value?.trim()) return value
	try {
		return JSON.stringify(JSON.parse(value), null, 2)
	} catch {
		return value
	}
}

/**
 * The Flow JSON textarea. Shows the cursor's line and column, which is how
 * Meta points at a problem, and pretty-prints the JSON on blur. The events
 * are caught on a wrapper as they bubble up, so the field's own handlers —
 * the blur that marks it as touched, above all — still run.
 */
function WAFlowJSONInputField({ name }: { name: string }) {
	const form = useForm()
	const [cursor, setCursor] = useState<{ line: number; column: number }>()

	const trackCursor = (event: React.SyntheticEvent) => {
		const target = event.target
		if (!(target instanceof HTMLTextAreaElement)) return
		const beforeCursor = target.value.slice(0, target.selectionStart)
		const lines = beforeCursor.split("\n")
		setCursor({
			line: lines.length,
			column: lines[lines.length - 1].length + 1,
		})
	}

	return (
		<Box
			onKeyUp={trackCursor}
			onClick={trackCursor}
			onFocus={trackCursor}
			onBlur={() => {
				setCursor(undefined)
				const value = form.getState().values[name] as string
				const formatted = formatFlowJSON(value)
				if (formatted !== value) {
					form.change(name, formatted)
				}
			}}
		>
			<TextAreaInputField
				name={name}
				label="Flow JSON"
				rows={20}
				fontFamily="mono"
				fontSize="sm"
				spellCheck={false}
				placeholder='{ "version": "7.0", "screens": [...] }'
				help={
					cursor
						? `Line ${cursor.line}, column ${cursor.column}`
						: "Formatted with 2-space indentation when you leave the field."
				}
				required
			/>
		</Box>
	)
}

const waFlowFormValidationSchema = Validator.object().shape({
	wa_business_account: Validator.mixed()
		.nullable()
		.required("Please select a business account."),
	name: Validator.string()
		.required("Please give the Flow a name.")
		.max(200, "Please keep the name within 200 characters."),
	categories: Validator.array()
		.nullable()
		.min(1, "Please pick at least one category."),
	flow_json: Validator.string()
		.required("Please paste the Flow JSON.")
		.test("json", "This is not a valid JSON object.", (value) =>
			isJSONObject(value || "")
		),
})

/**
 * A Flow's name, categories and JSON. Creating also picks the business
 * account; editing keeps it, since a Flow belongs to the account it was
 * created on. A published Flow takes only new JSON, which puts it back to
 * draft.
 */
function WAFlowEditorForm({
	mode,
	isPublished = false,
	integratedAppId,
	propsInitialValues,
	onCancel,
	onSubmit,
}: {
	mode: "create" | "edit"
	isPublished?: boolean
	integratedAppId?: number
	propsInitialValues?: Partial<IWAFlowFormValues>
	onCancel: () => void
	onSubmit: (
		values: IWAFlowFormValues & {
			wa_business_account: NonNullable<IWAFlowFormValues["wa_business_account"]>
		}
	) => Promise<void>
}) {
	const [initialValues] = useState<IWAFlowFormValues>(() => ({
		wa_business_account: propsInitialValues?.wa_business_account || null,
		name: propsInitialValues?.name || "",
		categories: propsInitialValues?.categories || [],
		flow_json: propsInitialValues?.flow_json || "",
	}))

	return (
		<Box paddingY="2" paddingX="4">
			<Form<IWAFlowFormValues>
				initialValues={initialValues}
				onSubmit={withServerErrors(async (values: IWAFlowFormValues, form) => {
					if (!values.wa_business_account) {
						throw new Error("Please fill all required fields.")
					}

					// Send exactly what the field shows, so the line and column
					// in Meta's errors point into the text on screen.
					const flowJson = formatFlowJSON(values.flow_json)
					if (flowJson !== values.flow_json) {
						form.change("flow_json", flowJson)
					}

					await onSubmit({
						...values,
						flow_json: flowJson,
						wa_business_account: values.wa_business_account,
					})
				})}
				validate={validateFormValues(waFlowFormValidationSchema)}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="6">
							{isPublished ? (
								<Alert status="warning" title="You're editing a published Flow">
									<Stack as="ul" gap="1" listStyleType="disc" paddingLeft="4">
										<li>
											Saving new JSON puts it back to draft. Until you publish
											it again, it cannot be sent to customers.
										</li>
										<li>
											Flows already sent keep the old version. Customers see the
											changes only in Flows sent after you republish.
										</li>
										<li>
											Meta resets the Flow's metrics. They will count only the
											newly published version.
										</li>
										<li>Its name and categories cannot be changed.</li>
									</Stack>
								</Alert>
							) : null}
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading>Business Account and Name</Heading>
										<Text>
											A Flow belongs to one business account. Categories tell
											Meta what the Flow is for.
										</Text>
									</Stack>
								</Col>
								<Col sm={12} md>
									<Stack gap="4">
										{mode === "create" ? (
											<SelectField
												name="wa_business_account"
												label="WhatsApp Business Account"
												select={SelectWABusinessAccount}
												fetchOnMount
												integratedAppId={integratedAppId}
												placeholder="Select a WhatsApp Business Account"
												maxWidth="sm"
											/>
										) : (
											<Stack gap="1">
												<Text fontWeight="semibold">
													WhatsApp Business Account
												</Text>
												<Text>{initialValues.wa_business_account?.name}</Text>
											</Stack>
										)}
										{isPublished ? (
											<>
												<Stack gap="1">
													<Text fontWeight="semibold">Flow Name</Text>
													<Text>{initialValues.name}</Text>
												</Stack>
												<Stack gap="1">
													<Text fontWeight="semibold">Categories</Text>
													<Text>
														{initialValues.categories
															.map((category) => category.name)
															.join(", ")}
													</Text>
												</Stack>
											</>
										) : (
											<>
												<TextInputField
													name="name"
													label="Flow Name"
													placeholder="e.g. Plan my trip"
													maxWidth="sm"
													required
												/>
												<SelectField
													select={Select}
													name="categories"
													label="Categories"
													options={waFlowCategoryOptions}
													multiple
													placeholder="Select categories"
													maxWidth="sm"
												/>
											</>
										)}
									</Stack>
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading>Start from a Preset</Heading>
										<Text>
											Pick a ready-made Flow for a trip plan request or a trip's
											feedback, choose its fields, and fill the Flow JSON below
											with it.
										</Text>
									</Stack>
								</Col>
								<Col sm={12} md>
									<WAFlowPresetPicker />
								</Col>
							</Grid>
							<Divider sm />
							<Grid gap="4">
								<Col sm={12} md={4}>
									<Stack gap="1">
										<Heading>Flow JSON</Heading>
										<Text>
											{mode === "create"
												? "Paste the Flow's JSON. It is created as a draft on Meta, so you can preview it before publishing."
												: "Saving replaces the draft's JSON on Meta. Preview it again before publishing."}
										</Text>
										<Box>
											<Text
												as="a"
												href="https://whautomate.com/tools/whatsapp-flow-builder"
												target="_blank"
												rel="noreferrer noopener"
												color="accent"
											>
												Build the JSON with WhatsApp Flow Builder{" "}
												<Icons.ArrowTopRightOnSquare />
											</Text>
										</Box>
										<Text color="muted" fontSize="sm">
											Answers reach us only through a "complete" action's
											payload. Name them the way a trip plan request does —
											destination, start_date, no_of_days, no_of_adults,
											no_of_children, comments — to fill those fields.
										</Text>
									</Stack>
								</Col>
								<Col sm={12} md>
									<Stack gap="4">
										<WAFlowJSONInputField name="flow_json" />
										<GetFieldValue<string> name="flow_json">
											{({ value }) => (
												<WAFlowJSONInspector flowJson={value || ""} />
											)}
										</GetFieldValue>
									</Stack>
								</Col>
							</Grid>

							<Divider sm />
							<Grid gap="4">
								<Col sm={12} md={{ offset: 4, span: 8 }}>
									<Stack paddingLeft={{ md: "4" }} gap="4">
										<SubmissionError />
										<Inline gap="4">
											<Button
												status="primary"
												type="submit"
												disabled={submitting}
											>
												{mode === "create"
													? submitting
														? "Creating Flow..."
														: "Create Draft Flow"
													: submitting
														? "Saving Flow..."
														: "Save Flow"}
											</Button>
											<Button
												onClick={onCancel}
												level="tertiary"
												disabled={submitting}
											>
												Cancel
											</Button>
										</Inline>
									</Stack>
								</Col>
							</Grid>
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	)
}

export function CreateWAFlowForm({
	integratedAppId,
	onDone,
}: {
	integratedAppId: number
	onDone: () => void
}) {
	const xhr = useXHR()
	return (
		<WAFlowEditorForm
			mode="create"
			integratedAppId={integratedAppId}
			onCancel={onDone}
			onSubmit={async (values) => {
				await xhr.post(`/integrations/whatsapp/${integratedAppId}/flows`, {
					wa_business_account_id: values.wa_business_account.id,
					name: values.name,
					categories: values.categories.map((category) => category.id),
					flow_json: values.flow_json,
				})
				showSnackbar("Flow created as a draft.")
				onDone()
			}}
		/>
	)
}

/**
 * The same editor over a draft or a published Flow. A deprecated Flow
 * cannot be edited, and the page says so rather than letting the save
 * fail.
 */
export function EditWAFlowForm({
	flowId,
	onDone,
}: {
	flowId: number | string
	onDone: () => void
}) {
	const xhr = useXHR()
	const { data: flow, error } = useSWR(
		`/wa-flows/${flowId}`,
		(url: string) =>
			xhr.get<{ data: TWAFlowRecord }>(url).then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	if (error) {
		return (
			<Alert status="error">
				{(error as Error)?.message ||
					"Something went wrong. Please try after sometime."}
			</Alert>
		)
	}

	if (!flow) {
		return <Spinner alignCenter padding="4" />
	}

	if (flow.status !== "DRAFT" && flow.status !== "PUBLISHED") {
		return (
			<Alert status="warning">
				Only a draft or published Flow can be edited. "{flow.name}" is{" "}
				{flow.status}.
			</Alert>
		)
	}

	return (
		<WAFlowEditorForm
			mode="edit"
			isPublished={flow.status === "PUBLISHED"}
			propsInitialValues={{
				wa_business_account: flow.wa_business_account,
				name: flow.name,
				categories: waFlowCategoryOptions.filter((option) =>
					flow.categories.includes(option.id)
				),
				flow_json: flow.definition
					? JSON.stringify(flow.definition, null, 2)
					: "",
			}}
			onCancel={onDone}
			onSubmit={async (values) => {
				await xhr.put(`/wa-flows/${flow.id}`, {
					name: values.name,
					categories: values.categories.map((category) => category.id),
					flow_json: values.flow_json,
				})
				showSnackbar("Flow saved.")
				onDone()
			}}
		/>
	)
}

function SyncWAFlowsMenu({
	integratedAppId,
	onQueued,
}: {
	integratedAppId: number
	onQueued: () => void
}) {
	const xhr = useXHR()
	return (
		<Component initialState={false}>
			{({ state, setState }) => (
				<>
					<Dropdown alignRight>
						<Dropdown.ToggleButton>
							<Icons.DotsVertical />
						</Dropdown.ToggleButton>
						<Dropdown.Menu>
							<Dropdown.MenuItem
								onClick={(e) => {
									e.preventDefault()
									setState(true)
								}}
							>
								<Icons.Refresh /> Sync Flows
							</Dropdown.MenuItem>
						</Dropdown.Menu>
					</Dropdown>
					<Dialog
						open={state}
						onClose={() => setState(false)}
						title="Sync Flows from Meta Business Accounts"
						sm
					>
						<Dialog.Body>
							<Form
								initialValues={{ app_id: integratedAppId }}
								onSubmit={withServerErrors(
									async (values: { app_id: number }) => {
										await xhr.post(
											`/integrations/whatsapp/${values.app_id}/sync-flows`
										)
										showSnackbar(
											"Flows will be updated shortly. Please check back in sometime."
										)
										setState(false)
										onQueued()
									}
								)}
							>
								{({ submitting, handleSubmit }) => (
									<form onSubmit={handleSubmit} noValidate>
										<Stack gap="4">
											<Text>
												Do you want to fetch Flows from the business accounts?
												System will read each Flow and its JSON from Meta.
											</Text>
											<SubmissionError />
											<Inline gap="4">
												<Button type="submit" disabled={submitting}>
													{submitting ? "Starting Process" : "Start Syncing"}
												</Button>
												<Button
													onClick={() => setState(false)}
													disabled={submitting}
												>
													Cancel
												</Button>
											</Inline>
										</Stack>
									</form>
								)}
							</Form>
						</Dialog.Body>
					</Dialog>
				</>
			)}
		</Component>
	)
}

export function WhatsAppMessagesList({
	integrationId,
	integratedAppId,
}: {
	integrationId: number
	integratedAppId: number
}) {
	const [query, setQuery] = useLocationQuery()
	const [searchParams, setSearchParams] = useSearch(query)

	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])

	return (
		<Search
			title="Messages"
			initialParams={searchParams}
			onSearch={(params) => {
				setSearchParams({ ...params, page: 1 })
			}}
		>
			{() => (
				<ListView<TWhatsAppMessage, TSearchParams & { include?: string }>
					pageKey={`integrations/${integrationId}/whatsapp/${integratedAppId}/messages`}
					params={{ ...searchParams, include: "rendered_html" }}
					onPageChange={(page: number) =>
						setSearchParams({ ...searchParams, page })
					}
					swrConfig={{ refreshInterval: 5000, revalidateOnFocus: true }}
					fetch={async (xhr, params) => {
						return xhr
							.get(`/wa-messages`, {
								params: {
									...params,
									integrated_app_id: integratedAppId,
								},
							})
							.then((resp) => resp.data)
					}}
				>
					{({ items }: { items: TWhatsAppMessage[] }) => (
						<>
							{items.length === 0 ? (
								<Alert status="info">
									<Stack gap="2">
										<Text fontWeight="semibold">No Messages Yet</Text>
										<Text>
											There are no messages using this integration. Once you
											send messages, they will appear here.
										</Text>
									</Stack>
								</Alert>
							) : (
								<Table
									bordered
									striped
									responsive
									headers={["From", "To", "Message", "Date", "Status"]}
									rows={items.map((msg: TWhatsAppMessage) => [
										<Stack gap="px">
											<Text>{msg.from_name || msg.from_number}</Text>
											{msg.from_name ? (
												<Text fontSize="sm" color="muted">
													{msg.from_number}
												</Text>
											) : null}
										</Stack>,
										<Stack gap="px">
											<Text>{msg.to_name || msg.to_number}</Text>
											{msg.to_name ? (
												<Text fontSize="sm" color="muted">
													{msg.to_number}
												</Text>
											) : null}
										</Stack>,
										msg.rendered_html ? (
											<Box
												maxWidth="sm"
												style={{ fontFamily: WHATSAPP_FONT_FAMILY }}
												dangerouslySetInnerHTML={{
													__html: msg.rendered_html,
												}}
											/>
										) : msg.message ? (
											<Text whiteSpace="preserveLine" maxWidth="sm">
												{msg.message}
											</Text>
										) : (
											<Badge warning>unknown</Badge>
										),
										<Time timestamp={msg.created_at} dateFormat timeFormat />,
										<Inline gap="2">
											{msg.statuses?.length ? (
												<Tooltip
													interactive
													content={
														<Box>
															<table>
																<tbody>
																	{msg.statuses.map((s) => (
																		<tr key={s.status}>
																			<td
																				style={{
																					paddingRight: "4px",
																					textTransform: "capitalize",
																				}}
																			>
																				{s.status}
																			</td>
																			<td>
																				{utcTimestampToLocalTimeString(
																					s.created_at
																				)}
																			</td>
																		</tr>
																	))}
																</tbody>
															</table>
														</Box>
													}
												>
													<Button inline>
														<Text textTransform="capitalize">{msg.status}</Text>
													</Button>
												</Tooltip>
											) : (
												<Text textTransform="capitalize">{msg.status}</Text>
											)}
											{msg.status === "failed" &&
											msg.statuses?.length &&
											msg.statuses.filter((s) => s.payload?.errors).length ? (
												<JSONPreviewInDialog
													title="Error Preview"
													bgColor="danger"
													color="danger"
													data={msg.statuses
														.flatMap((s) => s.payload?.errors)
														.filter((s): s is Record<string, unknown> =>
															Boolean(s)
														)}
												>
													{({ preview }) => (
														<Button
															onClick={() => preview()}
															status="danger"
															inline
														>
															<Icons.AttentionSolid />
														</Button>
													)}
												</JSONPreviewInDialog>
											) : null}
										</Inline>,
									])}
								/>
							)}
						</>
					)}
				</ListView>
			)}
		</Search>
	)
}

const sendTestMessageSchema = Validator.object().shape({
	recipient: Validator.string()
		.required("Phone number is required")
		.matches(
			/^\+?[1-9]\d{1,14}$/,
			"Please enter a valid phone number with country code (e.g., +918058400700)"
		),
})

function SendTestWAMessageInDialog({
	waTemplate,
	integratedAppId,
	onSuccess,
	children,
}: {
	waTemplate: TWATemplate
	integratedAppId: number
	onSuccess?: () => void
	children?: (props: { openDialog: () => void }) => React.ReactNode
}) {
	const xhr = useXHR()
	const [isDialogOpen, openDialog, closeDialog] = useDialog()

	const handleSendWATestMessage = useCallback(
		async (
			integratedAppId: number,
			data: {
				wa_template: number
				recipient: string
			}
		): Promise<{ message: string }> => {
			return xhr
				.post(
					`/integrations/whatsapp/${integratedAppId}/send-template-message`,
					data
				)
				.then((resp) => resp.data)
		},
		[xhr]
	)

	return (
		<>
			<Box>
				{children?.({
					openDialog: openDialog,
				})}
			</Box>
			<Dialog open={isDialogOpen} onClose={closeDialog}>
				<Dialog.Header>
					<Dialog.Title>Send Test Message</Dialog.Title>
				</Dialog.Header>
				<Form
					initialValues={{ recipient: "" }}
					validate={validateFormValues(sendTestMessageSchema)}
					onSubmit={withServerErrors(async (values: { recipient: string }) => {
						await handleSendWATestMessage(integratedAppId, {
							wa_template: waTemplate.id,
							recipient: values.recipient,
						})
						trackWhatsAppMessageSent({
							kind: "template",
							surface: "integration",
						})
						showSnackbar("Test message sent successfully!")
						onSuccess?.()
						closeDialog()
					})}
				>
					{({ handleSubmit, submitting }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Dialog.Body>
								<Stack gap="4">
									<Alert status="info">
										<Stack gap="2">
											<Text fontWeight="semibold">
												Template: {waTemplate.name}
											</Text>
											<Text fontSize="sm">
												A test message using this template will be sent to the
												phone number you provide below. VARIABLES will be
												replaced with EXAMPLE values.
											</Text>
											{waTemplate.wa_business_account ? (
												<Box>
													<Inline gap="1">
														<Badge>
															From: {waTemplate.wa_business_account.name}
														</Badge>
														<Badge>
															Phone:{" "}
															{
																waTemplate.wa_business_account
																	.wa_phone_numbers[0].display_phone_number
															}
														</Badge>
													</Inline>
												</Box>
											) : null}
											<WATemplatePreviewBubble
												template={waTemplate}
												mode="sample"
											/>
										</Stack>
									</Alert>
									<TextInputField
										name="recipient"
										label="Phone Number"
										placeholder="+918058400700"
										required
										help="Include country code (e.g., +1 for US, +91 for India)"
									/>

									<SubmissionError />
								</Stack>
							</Dialog.Body>
							<Dialog.Footer>
								<Inline gap="4">
									<Button type="submit" disabled={submitting}>
										{submitting ? "Sending..." : "Send Test Message"}
									</Button>
									<Button
										type="button"
										level="tertiary"
										onClick={closeDialog}
										disabled={submitting}
									>
										Cancel
									</Button>
								</Inline>
							</Dialog.Footer>
						</form>
					)}
				</Form>
			</Dialog>
		</>
	)
}

function useFreshId() {
	const [freshId, setKey] = useState(1)
	const refreshId = useCallback(() => {
		setKey((id) => id + 1)
	}, [])
	return [freshId, refreshId] as const
}

export function CreateWATemplateInDialog({
	integratedAppId,
	automationTemplate,
	onSuccess,
	children,
	open: openProp,
	onClose: onCloseProp,
}: Omit<
	React.ComponentProps<typeof WATemplateEditorForm>,
	"mode" | "onSubmit" | "integratedAppId" | "onCancel" | "availableLanguages"
> & {
	/** When provided, scopes the WABA listing inside the editor to this
	 *  integration. Omit for the inline-create flow on tenant-wide pages. */
	integratedAppId?: number
	/**
	 * When opened from the automation create flow, the editor is pre-filled
	 * from the automation template's defaults so the operator only fills in
	 * what's specific to this template (e.g. WABA, sample media).
	 */
	automationTemplate?: TWhatsAppAutomationTemplate | null
	onSuccess: (template: TWATemplate) => void
	/**
	 * Optional controlled open state. When provided, the dialog is driven
	 * externally and the children render-prop is unused. Lets callers like
	 * the automation create form open the dialog imperatively without the
	 * render-prop dance.
	 */
	open?: boolean
	onClose?: () => void
	children?: (props: {
		openDialog: () => void
		onClose: () => void
	}) => React.ReactNode
}) {
	const [internalOpen, openInternal, closeInternal] = useDialog()
	const isControlled = openProp !== undefined
	const isDialogOpen = isControlled ? Boolean(openProp) : internalOpen
	const openDialog = isControlled ? () => undefined : openInternal
	const closeDialog = isControlled
		? () => onCloseProp?.()
		: () => {
				closeInternal()
				onCloseProp?.()
			}
	const xhr = useXHR()
	const { data: bootstrapState, isLoading: isBootstrapLoading } = useSWR(
		isDialogOpen ? "/wa-message-templates/editor-bootstrap" : null,
		(url: string) =>
			xhr
				.get<{ data: TWAEditorBootstrapResponse }>(url)
				.then((resp) => resp.data.data)
	)

	return (
		<>
			<Box>
				{children?.({
					openDialog,
					onClose: closeDialog,
				})}
			</Box>
			<Dialog
				fitContainer
				open={isDialogOpen}
				onClose={closeDialog}
				title="Manage Whatsapp Template"
			>
				<Dialog.Body>
					{isBootstrapLoading || !bootstrapState ? (
						<Box paddingY="8">
							<Inline justifyContent="center">
								<Spinner />
							</Inline>
						</Box>
					) : (
						<WATemplateEditorForm
							mode="create"
							integratedAppId={integratedAppId}
							availableLanguages={bootstrapState.available_languages}
							seededFromAutomationTemplate={Boolean(automationTemplate)}
							propsInitialValues={{
								editor:
									automationTemplate?.default_whatsapp_template_editor ??
									bootstrapState.editor,
								automation_template: automationTemplate || null,
							}}
							onCancel={closeDialog}
							onSubmit={async (data) => {
								const resp = await xhr.post<{ data: TWATemplate }>(
									"/wa-message-templates",
									buildTemplateSubmissionData(data)
								)

								showSnackbar("WhatsApp Template Created successfully.")
								onSuccess(resp.data.data)
								closeDialog()
							}}
						/>
					)}
				</Dialog.Body>
			</Dialog>
		</>
	)
}

interface ITemplateFormValues {
	automation_template?: TWhatsAppAutomationTemplate | null
	wa_business_account?: TWhatsappBusinessAccount | null
	wa_phone_number?: TWhatsappBusinessAccountPhoneNumber | null
	editor?: IWhatsAppTemplateEditorValue
}

function hasLocalSampleMediaFile(
	sampleMedia?: TWhatsAppTemplateSampleMedia | null
): sampleMedia is TWhatsAppTemplateSampleMedia & { file: File } {
	return sampleMedia?.file instanceof File
}

function hasExistingSampleMedia(
	sampleMedia?: TWhatsAppTemplateSampleMedia | null
): sampleMedia is TWhatsAppTemplateSampleMedia {
	return Boolean(sampleMedia?.url)
}

function isMediaHeaderType(type?: string | null): boolean {
	return type === "image" || type === "video" || type === "document"
}

const waTemplateEditorFormValidationSchema = Validator.object().shape({
	automation_template: Validator.mixed().nullable().optional(),
	wa_business_account: Validator.mixed()
		.nullable()
		.required("Please select a valid whatsapp business account."),
	editor: Validator.object()
		.shape({
			name: Validator.string()
				.required("Please enter a template name.")
				.matches(
					/^[a-z0-9_]+$/,
					"Template name must contain only lowercase letters, numbers, and underscores"
				),
			category: Validator.string()
				.required("Please select a template category.")
				.oneOf(["UTILITY", "MARKETING", "AUTHENTICATION"])
				// Authentication-only components pin the category.
				.when(["authentication", "buttons"], {
					is: (
						authentication: IWhatsAppTemplateEditorValue["authentication"],
						buttons: IWhatsAppTemplateEditorValue["buttons"]
					) =>
						Boolean(
							authentication?.code_expiration_enabled ||
							authentication?.message_validity_enabled ||
							buttons?.some((button) => button.type === "copy_code")
						),
					then: (schema) =>
						schema.oneOf(
							["AUTHENTICATION"],
							"Authentication-only components require the AUTHENTICATION category."
						),
				}),
			language: Validator.string()
				.required("Please select a language.")
				.max(10, "Please select a valid language code"),
			// Rules that depend on a sibling sit on the field they concern, so
			// the error shows under that field's input.
			header: Validator.object().shape({
				type: Validator.string()
					.required("Please select a header type.")
					.oneOf(["none", "text", "image", "video", "document"]),
				text: Validator.string()
					.nullable()
					.max(60)
					.when("type", {
						is: "text",
						then: (schema) =>
							schema.trim().required("Please enter the header text."),
					}),
				image_mode: Validator.string().nullable().oneOf(["static", "dynamic"]),
				dynamic_image_source_key: Validator.string()
					.nullable()
					.when(["type", "image_mode"], {
						is: (type: string, imageMode: string) =>
							isMediaHeaderType(type) && imageMode === "dynamic",
						then: (schema) =>
							schema
								.trim()
								.required(
									"Please pick the variable the header image comes from."
								),
					}),
				sample_media_id: Validator.number().nullable(),
				// The sample is one of an uploaded file, an already stored one,
				// or its id; any of the three satisfies WhatsApp's review.
				sample_media: Validator.mixed()
					.nullable()
					.when(["type", "sample_media_id"], {
						is: (type: string, sampleMediaId: number | null) =>
							isMediaHeaderType(type) && !sampleMediaId,
						then: (schema) =>
							schema.test(
								"sample-media-present",
								"Please upload a sample file for WhatsApp to review.",
								(value) =>
									hasLocalSampleMediaFile(
										value as TWhatsAppTemplateSampleMedia | null
									) ||
									hasExistingSampleMedia(
										value as TWhatsAppTemplateSampleMedia | null
									)
							),
					}),
			}),
			body: Validator.object().shape({
				text: Validator.string()
					.required("Message body is required")
					.test(
						"not-empty",
						"Message body cannot be empty or contain only whitespace",
						(value) => (value ? value.trim().length > 0 : false)
					)
					.min(1, "Message body must have at least 1 character")
					.max(1024, "Message body must be 1024 characters or less"),
			}),
			footer: Validator.object().shape({
				enabled: Validator.boolean().required(),
				text: Validator.string()
					.nullable()
					.max(60)
					.when("enabled", {
						is: true,
						then: (schema) =>
							schema
								.trim()
								.required("Please enter footer text or disable the footer."),
					}),
			}),
			buttons: Validator.array()
				.of(
					Validator.object().shape({
						type: Validator.string()
							.required()
							.oneOf([
								"quick_reply",
								"url",
								"phone_number",
								"copy_code",
								"flow",
							]),
						text: Validator.string().nullable().max(25),
						url: Validator.string().nullable(),
						phone_number: Validator.string().nullable().max(20),
						code: Validator.string().nullable().max(15),
					})
				)
				.max(10),
			authentication: Validator.object().shape({
				security_recommendation: Validator.boolean().required(),
				code_expiration_enabled: Validator.boolean().required(),
				code_expiration_minutes: Validator.number().nullable().min(1).max(90),
				message_validity_enabled: Validator.boolean().required(),
				message_validity_seconds: Validator.number()
					.nullable()
					.min(60)
					.max(900),
			}),
		})
		.required("Please enter a valid template."),
})

function createEmptyTemplateEditorValue(): IWhatsAppTemplateEditorValue {
	return {
		name: "",
		category: "UTILITY",
		language: "en",
		automation_template_key: null,
		header: {
			type: "none",
			text: "",
			registration_handle: "",
			static_image_url: "",
			image_mode: "static",
			dynamic_image_source_key: "",
			sample_media_id: null,
			sample_media: null,
		},
		body: {
			text: "",
		},
		footer: {
			enabled: false,
			text: "",
		},
		buttons: [],
		authentication: {
			security_recommendation: true,
			code_expiration_enabled: false,
			code_expiration_minutes: 5,
			message_validity_enabled: false,
			message_validity_seconds: 600,
		},
	}
}

function linkEditorToAutomationTemplate(
	editor: IWhatsAppTemplateEditorValue,
	automationTemplate?: TWhatsAppAutomationTemplate | null
): IWhatsAppTemplateEditorValue {
	return {
		...editor,
		automation_template_key: automationTemplate?.key || null,
	}
}

function serializeEditorForSubmission(editor: IWhatsAppTemplateEditorValue) {
	const sampleMedia = editor.header.sample_media || null

	return {
		...editor,
		header: {
			type: editor.header.type,
			text: editor.header.text,
			registration_handle: editor.header.registration_handle,
			static_image_url: editor.header.static_image_url,
			image_mode: editor.header.image_mode,
			dynamic_image_source_key: editor.header.dynamic_image_source_key,
			sample_media_id: editor.header.sample_media_id,
			sample_media: sampleMedia
				? {
						id: sampleMedia.id || null,
						url: sampleMedia.url || null,
						mime_type: sampleMedia.mime_type || null,
						file_name: sampleMedia.file_name || null,
						name: sampleMedia.name || null,
						preview_url: sampleMedia.preview_url || null,
					}
				: null,
		},
	}
}

function buildTemplateSubmissionData(data: {
	wa_business_account: { id: number }
	editor: IWhatsAppTemplateEditorValue
	automation_template?: TWhatsAppAutomationTemplate | null
	wa_phone_number?: TWhatsappBusinessAccountPhoneNumber | null
}) {
	const formData = new FormData()
	const uploadedFile = hasLocalSampleMediaFile(data.editor.header.sample_media)
		? data.editor.header.sample_media.file
		: null

	formData.append("wa_business_account", String(data.wa_business_account.id))
	formData.append(
		"editor",
		JSON.stringify({
			...serializeEditorForSubmission(data.editor),
			automation_template_key: data.automation_template?.key || null,
		})
	)

	if (data.wa_phone_number?.id) {
		formData.append("wa_phone_number_id", String(data.wa_phone_number.id))
	}

	if (uploadedFile) {
		formData.append("sample_media", uploadedFile)
	}

	return formData
}

/**
 * The editor on a page of its own. A template is a long form — components,
 * buttons, sample values, media — so it gets the full width, and leaving it
 * is a navigation the browser's back button understands.
 */
export function CreateWATemplateForm({
	integratedAppId,
	onDone,
}: {
	integratedAppId: number
	onDone: () => void
}) {
	const xhr = useXHR()
	const { data: bootstrapState, error } = useSWR(
		"/wa-message-templates/editor-bootstrap",
		(url: string) =>
			xhr
				.get<{ data: TWAEditorBootstrapResponse }>(url)
				.then((resp) => resp.data.data)
	)

	if (error) {
		return (
			<Alert status="error">
				{(error as Error)?.message ||
					"Something went wrong. Please try after sometime."}
			</Alert>
		)
	}

	if (!bootstrapState) {
		return <Spinner alignCenter padding="4" />
	}

	return (
		<WATemplateEditorForm
			mode="create"
			integratedAppId={integratedAppId}
			availableLanguages={bootstrapState.available_languages}
			propsInitialValues={{ editor: bootstrapState.editor }}
			onCancel={onDone}
			onSubmit={async (data) => {
				await xhr.post<{ data: TWATemplate }>(
					"/wa-message-templates",
					buildTemplateSubmissionData(data)
				)

				showSnackbar("WhatsApp Template Created successfully.")
				onDone()
			}}
		/>
	)
}

/**
 * The same editor over an existing template. Not every template can be
 * edited here — one authored outside Sembark may use components the editor
 * cannot round-trip — and the endpoint says so rather than letting the
 * operator overwrite it.
 */
export function EditWATemplateForm({
	integratedAppId,
	templateId,
	onDone,
}: {
	integratedAppId: number
	templateId: string
	onDone: () => void
}) {
	const xhr = useXHR()
	const { data: editorState, error } = useSWR(
		`/wa-message-templates/${templateId}/editor`,
		(url: string) =>
			xhr
				.get<{ data: TWAEditorTemplateResponse }>(url)
				.then((resp) => resp.data.data),
		{ revalidateOnFocus: false }
	)

	if (error) {
		return (
			<Alert status="error">
				{(error as Error)?.message || "Unable to load template editor data."}
			</Alert>
		)
	}

	if (!editorState) {
		return <Spinner alignCenter padding="4" />
	}

	if (editorState.editor_supported === false || !editorState.editor) {
		return (
			<Alert status="warning">
				{editorState.editor_unsupported_reason ||
					"This template can't be edited safely in the current editor."}
			</Alert>
		)
	}

	return (
		<WATemplateEditorForm
			mode="edit"
			integratedAppId={integratedAppId}
			availableLanguages={editorState.available_languages}
			propsInitialValues={{
				automation_template: editorState.automation_template || null,
				wa_business_account: editorState.template.wa_business_account || null,
				wa_phone_number: editorState.template.phone_number || null,
				editor: editorState.editor,
			}}
			onCancel={onDone}
			onSubmit={async (data) => {
				await xhr.put(
					`/wa-message-templates/${templateId}`,
					buildTemplateSubmissionData(data)
				)

				showSnackbar("WhatsApp Template updated successfully.")
				onDone()
			}}
		/>
	)
}

function EditWATemplateLinksInDialog({
	integratedAppId,
	template,
	onSuccess,
	children,
}: {
	integratedAppId: number
	template: TWATemplate
	onSuccess: () => void
	children: (props: { openDialog: () => void }) => React.ReactNode
}) {
	const [isDialogOpen, openDialog, closeDialog] = useDialog()
	const xhr = useXHR()
	const businessAccount = template.wa_business_account

	type TLinksFormValues = {
		automation_template?: TWhatsAppAutomationTemplate | null
		wa_phone_number?: TWhatsappBusinessAccountPhoneNumber | null
	}

	const initialValues: TLinksFormValues = {
		// Seed the full {key, name} so the picker shows the currently-linked
		// template pre-selected (it matches options by name). Fall back to a
		// key-only object if the name isn't on the row.
		automation_template: template.automation_template
			? (template.automation_template as TWhatsAppAutomationTemplate)
			: template.automation_template_key
				? ({
						key: template.automation_template_key,
					} as TWhatsAppAutomationTemplate)
				: null,
		wa_phone_number: template.phone_number || null,
	}

	return (
		<>
			<Box>{children({ openDialog })}</Box>
			<Dialog open={isDialogOpen} onClose={closeDialog} title="Link Template">
				<Dialog.Body>
					<Form<TLinksFormValues>
						initialValues={initialValues}
						onSubmit={withServerErrors(async (values) => {
							await xhr.patch(`/wa-message-templates/${template.id}/links`, {
								automation_template_key:
									values.automation_template?.key || null,
								wa_phone_number_id: values.wa_phone_number?.id || null,
							})
							showSnackbar("Template links updated.")
							onSuccess()
							closeDialog()
						})}
						subscription={{ submitting: true }}
					>
						{({ handleSubmit, submitting }) => (
							<form noValidate onSubmit={handleSubmit}>
								<Stack gap="4">
									<Text fontSize="sm" color="muted">
										These linkages are local to your Dashboard and help in
										different automations and variable resolutions. Editing them
										won't modify the template content.
									</Text>
									<SelectField
										name="automation_template"
										label="Automation Template"
										secondaryLabel="optional"
										select={SelectWhatsAppAutomationTemplate}
										fetchOnMount
										placeholder="Select an automation template"
										help="Variables in the template must match this source. Otherwise the link is rejected."
									/>
									<SelectField
										name="wa_phone_number"
										label="Default Sender Phone Number"
										secondaryLabel="optional"
										select={SelectWAPhoneNumber}
										integratedAppId={integratedAppId}
										waBusinessAccountId={businessAccount?.id}
										placeholder="Pick a default phone number"
										help="Pre-selected when sending this template. Must belong to the template's WA business account."
									/>
									<Divider marginY="0" />
									<SubmissionError />
									<Inline gap="4">
										<Button
											type="submit"
											status="primary"
											disabled={submitting}
										>
											{submitting ? "Saving..." : "Save Links"}
										</Button>
										<Button
											type="button"
											level="tertiary"
											onClick={closeDialog}
											disabled={submitting}
										>
											Cancel
										</Button>
									</Inline>
								</Stack>
							</form>
						)}
					</Form>
				</Dialog.Body>
			</Dialog>
		</>
	)
}

function WATemplateEditorForm({
	mode,
	integratedAppId,
	availableLanguages,
	onCancel,
	onSubmit,
	propsInitialValues,
	seededFromAutomationTemplate,
}: {
	mode: "create" | "edit"
	/** When provided, scopes the WABA listing to this integration. Optional
	 *  for inline create flows that aren't anchored to one. */
	integratedAppId?: number
	availableLanguages: TWAEditorTemplateResponse["available_languages"]
	onCancel: () => void
	onSubmit: (
		data: ITemplateFormValues & {
			wa_business_account: NonNullable<
				ITemplateFormValues["wa_business_account"]
			>
			editor: NonNullable<ITemplateFormValues["editor"]>
		}
	) => Promise<void>
	propsInitialValues?: ITemplateFormValues
	/**
	 * The editor was opened from the automation create flow, so the linked
	 * automation template arrived pre-selected. Only changes what the field
	 * says — the operator may still pick another, since the link decides which
	 * source fills the variables rather than which automation may send it.
	 */
	seededFromAutomationTemplate?: boolean
}) {
	const [initialValues] = useState<ITemplateFormValues>(() => ({
		automation_template: propsInitialValues?.automation_template || null,
		wa_business_account: propsInitialValues?.wa_business_account || null,
		wa_phone_number: propsInitialValues?.wa_phone_number || null,
		editor: propsInitialValues?.editor || createEmptyTemplateEditorValue(),
	}))
	const xhr = useXHR()
	const renderHtml = useCallback(
		(value: IWhatsAppTemplateEditorValue) =>
			xhr
				.post<{
					rendered_html: string
				}>("/wa-message-templates/preview", {
					editor: value,
					mode: "raw",
				})
				.then((resp) => resp.data.rendered_html || ""),
		[xhr]
	)

	if (initialValues.editor?.category === "AUTHENTICATION") {
		return (
			<Alert
				status="error"
				title="Creating/Editing Authentication Template is not supported yet."
			>
				To create Authentication Templates, please visit your Meta Business
				Account.
			</Alert>
		)
	}

	return (
		<Box paddingY="2" paddingX="4">
			<Form<ITemplateFormValues>
				initialValues={initialValues}
				onSubmit={withServerErrors(async (values: ITemplateFormValues) => {
					if (!values.wa_business_account) {
						throw new Error("Please fill all required fields.")
					}

					if (!values.editor) {
						throw new Error("Please enter a valid template.")
					}

					await onSubmit({
						...values,
						wa_business_account: values.wa_business_account,
						editor: values.editor,
						automation_template: values.automation_template,
						wa_phone_number: values.wa_phone_number,
					})
				})}
				validate={validateFormValues(waTemplateEditorFormValidationSchema)}
				subscription={{ submitting: true }}
			>
				{({ handleSubmit, submitting, form }) => (
					<form noValidate onSubmit={handleSubmit}>
						<Stack gap="2">
							<Stack gap="4">
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Stack gap="1">
											<Heading>Business Account and Templates</Heading>
											<Text>
												Please select a business account, phone number and an
												automation template to auto-fill the default content for
												the template.
											</Text>
										</Stack>
									</Col>
									<Col sm={12} md>
										<Stack gap="4">
											<Grid noGutters gap="4">
												<Col sm={12} md="auto">
													<SelectField
														name="wa_business_account"
														label="WhatsApp Business Account"
														select={SelectWABusinessAccount}
														fetchOnMount
														integratedAppId={integratedAppId}
														placeholder="Select a WhatsApp Business Account"
														help="Select the WhatsApp Business Account you want to connect"
														maxWidth={"sm"}
														onChange={(
															value: ITemplateFormValues["wa_business_account"]
														) => {
															form.change("wa_business_account", value)
															if (value?.wa_phone_numbers.length) {
																form.change(
																	"wa_phone_number",
																	value.wa_phone_numbers[0]
																)
															} else {
																form.change("wa_phone_number", undefined)
															}
														}}
													/>
												</Col>
												<Col>
													<GetFieldValue<
														ITemplateFormValues["wa_business_account"]
													> name="wa_business_account">
														{({ value: businessAccount }) => (
															<SelectField
																name="wa_phone_number"
																label="Default Sender Phone Number"
																secondaryLabel="optional"
																select={SelectWAPhoneNumber}
																integratedAppId={integratedAppId}
																waBusinessAccountId={businessAccount?.id}
																placeholder="Pick a default phone number"
																help="Pre-selected when sending. Local-only — Meta isn't told."
																maxWidth={"sm"}
															/>
														)}
													</GetFieldValue>
												</Col>
											</Grid>

											<GetFieldValue<
												ITemplateFormValues["editor"]
											> name="editor">
												{({ value: editorValue }) =>
													editorValue?.category !== "AUTHENTICATION" ? (
														<>
															<SelectField
																name="automation_template"
																label="Automation Template"
																secondaryLabel="optional"
																select={SelectWhatsAppAutomationTemplate}
																fetchOnMount
																placeholder="Select a WhatsApp automation template"
																help={
																	seededFromAutomationTemplate
																		? "Pre-selected from the automation you're creating. Change it to write the message against another source's variables — the template stays usable by any automation that can fill them."
																		: "Choose the automation template that provides the default WhatsApp content and variables"
																}
																onChange={(value: unknown) => {
																	const picked = value as
																		| TWhatsAppAutomationTemplate
																		| null
																		| undefined

																	// The listing carries summaries only; the
																	// editor seed and the variable catalogue
																	// come with the template's detail.
																	const apply = (
																		selectedAutomationTemplate: TWhatsAppAutomationTemplate | null
																	) =>
																		form.batch(() => {
																			form.change(
																				"automation_template" as never,
																				(selectedAutomationTemplate ||
																					null) as never
																			)
																			form.change(
																				"editor" as never,
																				(mode === "edit"
																					? linkEditorToAutomationTemplate(
																							editorValue ||
																								createEmptyTemplateEditorValue(),
																							selectedAutomationTemplate || null
																						)
																					: (selectedAutomationTemplate?.default_whatsapp_template_editor ??
																						createEmptyTemplateEditorValue())) as never
																			)
																		})

																	if (!picked?.key) {
																		apply(null)
																		return
																	}

																	form.change(
																		"automation_template" as never,
																		picked as never
																	)
																	void xhr
																		.get<{
																			data: TWhatsAppAutomationTemplate
																		}>(`/automation-templates/${picked.key}`)
																		.then((resp) => apply(resp.data.data))
																		.catch((error) =>
																			showSnackbar(
																				(error as Error)?.message ||
																					"Unable to load the automation template."
																			)
																		)
																}}
															/>
															<GetFieldValue<
																ITemplateFormValues["automation_template"]
															> name="automation_template">
																{({ value: linkedAutomationTemplate }) => (
																	<Inline gap="2" flexWrap="wrap">
																		{linkedAutomationTemplate ? (
																			<Button
																				level="secondary"
																				size="sm"
																				type="button"
																				onClick={() => {
																					form.batch(() => {
																						form.change(
																							"automation_template" as never,
																							null as never
																						)
																						form.change(
																							"editor" as never,
																							(mode === "edit"
																								? linkEditorToAutomationTemplate(
																										editorValue ||
																											createEmptyTemplateEditorValue(),
																										null
																									)
																								: createEmptyTemplateEditorValue()) as never
																						)
																					})
																				}}
																			>
																				<Icons.Trash /> Clear Template
																			</Button>
																		) : null}
																		{mode === "edit" &&
																		linkedAutomationTemplate?.key ? (
																			<Button
																				level="secondary"
																				size="sm"
																				type="button"
																				title="Replace the header, body, footer and buttons with the template's current defaults. Name, language and category stay. Nothing is sent to Meta until you save."
																				onClick={() => {
																					const key =
																						linkedAutomationTemplate.key
																					void xhr
																						.get<{
																							data: TWhatsAppAutomationTemplate
																						}>(`/automation-templates/${key}`)
																						.then((resp) => {
																							const defaults =
																								resp.data.data
																									.default_whatsapp_template_editor
																							if (!defaults) {
																								showSnackbar(
																									"This automation template has no default message to reset to."
																								)
																								return
																							}
																							const current =
																								editorValue ||
																								createEmptyTemplateEditorValue()
																							form.change(
																								"editor" as never,
																								{
																									...defaults,
																									// The template's identity at
																									// Meta stays what it is.
																									name: current.name,
																									language: current.language,
																									category: current.category,
																									automation_template_key: key,
																								} as never
																							)
																							showSnackbar(
																								"Message reset to the template's defaults. Review the preview, then save to send the change to Meta."
																							)
																						})
																						.catch((error) =>
																							showSnackbar(
																								(error as Error)?.message ||
																									"Unable to load the automation template."
																							)
																						)
																				}}
																			>
																				<Icons.Refresh /> Reset to Default
																				Template
																			</Button>
																		) : null}
																	</Inline>
																)}
															</GetFieldValue>
														</>
													) : null
												}
											</GetFieldValue>
										</Stack>
									</Col>
								</Grid>

								<GetFieldValue<ITemplateFormValues["editor"]> name="editor">
									{({ value: editorValue }) => (
										<GetFieldValue<
											ITemplateFormValues["automation_template"]
										> name="automation_template">
											{({ value: automationTemplate }) => {
												const source =
													editorValue?.category === "AUTHENTICATION"
														? null
														: automationTemplate?.template_data_source || null

												return (
													<GetFieldValue<
														ITemplateFormValues["wa_business_account"]
													> name="wa_business_account">
														{({ value: businessAccount }) => (
															<WhatsAppTemplateInputField
																variableContext={{
																	name:
																		automationTemplate?.name ||
																		"Custom WhatsApp Template",
																	available_parameters:
																		source?.available_parameters || [],
																	wildcard_prefix:
																		source?.wildcard_key_config?.prefix || null,
																}}
																availableLanguages={availableLanguages}
																name="editor"
																renderHtml={renderHtml}
																waBusinessAccountId={businessAccount?.id}
																onValueChange={(nextValue) => {
																	if (
																		nextValue.category === "AUTHENTICATION" &&
																		automationTemplate
																	) {
																		form.change(
																			"automation_template" as never,
																			null as never
																		)
																	}
																}}
															/>
														)}
													</GetFieldValue>
												)
											}}
										</GetFieldValue>
									)}
								</GetFieldValue>

								<Divider sm />
								<SubmissionError />
							</Stack>

							<Inline justifyContent="end" gap="2">
								<Button
									onClick={onCancel}
									level="tertiary"
									disabled={submitting}
								>
									Cancel
								</Button>

								<Button status="primary" type="submit" disabled={submitting}>
									<Icons.DocumentDownload />{" "}
									{submitting ? "Saving Template..." : "Save Template"}
								</Button>
							</Inline>
						</Stack>
					</form>
				)}
			</Form>
		</Box>
	)
}

function SelectWhatsAppAutomationTemplate({
	...props
}: Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()

	const fetchAutomationTemplates = useCallback(
		async (
			params: Record<string, unknown>
		): Promise<IListResponse<TWhatsAppAutomationTemplate>> => {
			return xhr
				.get(`/automation-templates`, {
					params: {
						...params,
						// A WhatsApp template can only be the message of a template
						// that sends one; templates that offer a form or create a
						// record are left out.
						sends_whatsapp_template: 1,
					},
				})
				.then((resp) => resp.data)
		},
		[xhr]
	)

	return (
		<AsyncSelect
			{...props}
			labelKey="name"
			optionRenderer={({ option }: { option: TWhatsAppAutomationTemplate }) => (
				<Stack gap="1">
					<Inline gap="2" alignItems="center" flexWrap="wrap">
						<Text fontWeight="semibold">{option.name}</Text>
					</Inline>
					<Text fontSize="sm" color="muted">
						{option.description}
					</Text>
				</Stack>
			)}
			fetch={async (q: string) => {
				const resp = await fetchAutomationTemplates({ q })

				return resp.data.filter(
					(item: TWhatsAppAutomationTemplate) => item.channel === "whatsapp"
				)
			}}
		/>
	)
}
